import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import productService from '../services/productService';
import companyService from '../services/companyService'; // kept for updateSettings (requireIngredientQty toggle)
import ingredientService from '../services/ingredientService';
import documentService from '../services/documentService';
import analyticsService from '../services/analyticsService';
import qboService from '../services/qboService';
import xeroService from '../services/xeroService';
import baseRecipeService from '../services/baseRecipeService';
import techSheetService from '../services/techSheetService';
import supplierService from '../services/supplierService';
import SearchableSelect from '../components/SearchableSelect';
import { usePermissions } from '../context/PermissionContext';
import NutritionFactsPanel from '../components/NutritionFactsPanel';
import IngredientsHub from '../components/IngredientsHub';
import SuppliersHub from '../components/SuppliersHub';
import TechSheetPanel from '../components/TechSheetPanel';
import ConfirmModal from '../components/ConfirmModal';
import { compressImage } from '../utils/imageCompress';

// ── Recipe editor ─────────────────────────────────────────────────────────────
function RecipeEditor({ ingredients, recipeItems, onChange, priceMap = {}, baseRecipeCost = null }) {
  const [search, setSearch] = useState('');
  const selectedIds = new Set(recipeItems.map(r => String(r.ingredientId)));
  const filtered = search.trim()
    ? ingredients.filter(i =>
        i.name.toLowerCase().includes(search.toLowerCase()) ||
        (i.Manufacturer?.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (i.rawMaterialCode || '').toLowerCase().includes(search.toLowerCase())
      )
    : [...ingredients];
  const visibleIngredients = filtered.sort(
    (a, b) => (selectedIds.has(String(a.id)) ? 0 : 1) - (selectedIds.has(String(b.id)) ? 0 : 1)
  );

  const toggle = (ingredientId) => {
    const id = String(ingredientId);
    if (selectedIds.has(id)) {
      onChange(recipeItems.filter(r => String(r.ingredientId) !== id));
    } else {
      onChange([...recipeItems, { ingredientId: Number(id), expectedQuantityKg: '' }]);
    }
  };

  const setQty = (ingredientId, value) => {
    const id = String(ingredientId);
    onChange(recipeItems.map(r =>
      String(r.ingredientId) === id ? { ...r, expectedQuantityKg: value } : r
    ));
  };

  const getQty = (ingredientId) => {
    const row = recipeItems.find(r => String(r.ingredientId) === String(ingredientId));
    return row ? row.expectedQuantityKg : '';
  };

  // Compute estimated total cost per unit (direct ingredients + base recipe)
  let estimatedTotal = null;
  for (const r of recipeItems) {
    const qty = Number(r.expectedQuantityKg);
    const price = priceMap[String(r.ingredientId)];
    if (qty > 0 && price != null) {
      estimatedTotal = (estimatedTotal || 0) + qty * price;
    }
  }
  if (baseRecipeCost != null) {
    estimatedTotal = (estimatedTotal || 0) + baseRecipeCost;
  }

  return (
    <div>
      <input
        type="text"
        className="form-control form-control-sm mb-2"
        placeholder="Search ingredients…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
      <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 4 }}>
        <table className="table table-sm mb-0">
          <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <tr>
              <th style={{ width: 36 }}></th>
              <th>Ingredient</th>
              <th>Manufacturer</th>
              <th style={{ width: 90 }}>RM Code</th>
              <th style={{ width: 150 }}>Qty / unit (kg)</th>
              <th style={{ width: 110 }} className="text-end">Cost / unit</th>
            </tr>
          </thead>
          <tbody>
            {visibleIngredients.map(ing => {
              const checked = selectedIds.has(String(ing.id));
              const qty = Number(getQty(ing.id));
              const price = priceMap[String(ing.id)];
              const cost = checked && qty > 0 && price != null ? (qty * price).toFixed(4) : null;
              return (
                <tr key={ing.id} style={{ background: checked ? '#f0fff4' : undefined }}>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={checked}
                      onChange={() => toggle(ing.id)}
                    />
                  </td>
                  <td style={{ fontWeight: checked ? 600 : undefined }}>{ing.name}</td>
                  <td className="text-muted" style={{ fontSize: 13 }}>{ing.Manufacturer?.name}</td>
                  <td style={{ fontSize: 12 }}>
                    {ing.rawMaterialCode
                      ? <span className="badge text-bg-light text-dark border">{ing.rawMaterialCode}</span>
                      : <span className="text-muted">—</span>}
                  </td>
                  <td>
                    {checked && (
                      <div className="input-group input-group-sm">
                        <input
                          type="number"
                          className="form-control"
                          min="0"
                          step="0.0001"
                          placeholder="0.0000"
                          value={getQty(ing.id)}
                          onChange={e => setQty(ing.id, e.target.value)}
                        />
                        <span className="input-group-text">kg</span>
                      </div>
                    )}
                  </td>
                  <td className="text-end" style={{ fontSize: 13 }}>
                    {checked
                      ? (cost != null
                        ? <span className="text-success fw-semibold">${cost}</span>
                        : <span className="text-muted">—</span>)
                      : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {estimatedTotal != null && (
        <div className="mt-2 text-end">
          <span className="badge text-bg-secondary" style={{ fontSize: 13 }}>
            Estimated cost / unit: ${estimatedTotal.toFixed(4)}
          </span>
        </div>
      )}
    </div>
  );
}

// Returns the cost contribution of one base recipe assignment to a product unit.
// Returns null when data is insufficient (no recipe selected, no qty, no priced ingredients).
function computeBaseRecipeCostPerUnit(ref, allBaseRecipes, priceMap) {
  if (!ref.baseRecipeId || !Number(ref.displayQty)) return null;
  const br = allBaseRecipes.find(b => String(b.id) === String(ref.baseRecipeId));
  if (!br || !Number(br.totalBatchWeightKg)) return null;
  let ingCost = 0;
  for (const ing of br.Ingredients || []) {
    const qty = Number(ing.expectedQuantityKg);
    const price = priceMap[String(ing.ingredientId)];
    if (qty > 0 && price != null) ingCost += qty * price;
  }
  if (!ingCost) return null;
  const quantityKg = ref.unit === 'g' ? Number(ref.displayQty) / 1000 : Number(ref.displayQty);
  return quantityKg * (ingCost / Number(br.totalBatchWeightKg));
}

// ── Base Recipes section (embedded in Products & Ingredients page) ────────────
const BLANK_BR_FORM = { name: '', description: '', totalBatchWeightKg: '' };
const BLANK_BR_ING  = { ingredientId: '', displayQty: '', unit: 'g' };

function BaseRecipeModal({ show, onClose, onSaved, recipe, ingredients }) {
  const [form, setForm]     = useState(BLANK_BR_FORM);
  const [lines, setLines]   = useState([{ ...BLANK_BR_ING }]);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (!show) return;
    if (recipe) {
      setForm({ name: recipe.name || '', description: recipe.description || '', totalBatchWeightKg: recipe.totalBatchWeightKg ?? '' });
      setLines((recipe.Ingredients || []).length > 0
        ? recipe.Ingredients.map(i => ({ ingredientId: String(i.ingredientId), displayQty: i.expectedQuantityKg ?? '', unit: 'kg' }))
        : [{ ...BLANK_BR_ING }]);
    } else {
      setForm(BLANK_BR_FORM);
      setLines([{ ...BLANK_BR_ING }]);
    }
    setError('');
  }, [show, recipe]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setLine = (idx, k, v) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, [k]: v } : l));
  const addLine = () => setLines(ls => [...ls, { ...BLANK_BR_ING }]);
  const removeLine = (idx) => setLines(ls => ls.filter((_, i) => i !== idx));
  const toggleUnit = (idx) => setLines(ls => ls.map((l, i) => {
    if (i !== idx) return l;
    const val = parseFloat(l.displayQty);
    if (l.unit === 'kg') {
      return { ...l, unit: 'g', displayQty: isNaN(val) ? '' : String(parseFloat((val * 1000).toFixed(4))) };
    } else {
      return { ...l, unit: 'kg', displayQty: isNaN(val) ? '' : String(parseFloat((val / 1000).toFixed(4))) };
    }
  }));

  const ingOptions = ingredients.map(i => ({ value: String(i.id), label: i.name }));

  const ingredientSumKg = lines.reduce((sum, l) => {
    const q = Number(l.displayQty);
    if (!(q > 0)) return sum;
    return sum + (l.unit === 'g' ? q / 1000 : q);
  }, 0);
  const totalBatchWeightKg = Number(form.totalBatchWeightKg) || 0;
  const weightDiffKg = ingredientSumKg - totalBatchWeightKg;
  const weightMismatch = totalBatchWeightKg > 0 && Math.abs(weightDiffKg) > 0.0005;

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!form.totalBatchWeightKg || Number(form.totalBatchWeightKg) <= 0) { setError('Total batch weight must be > 0.'); return; }
    const validLines = lines.filter(l => l.ingredientId && Number(l.displayQty) > 0);
    setSaving(true); setError('');
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        totalBatchWeightKg: Number(form.totalBatchWeightKg),
        ingredients: validLines.map((l, idx) => ({
          ingredientId: Number(l.ingredientId),
          expectedQuantityKg: l.unit === 'g' ? Number(l.displayQty) / 1000 : Number(l.displayQty),
          sortOrder: idx,
        })),
      };
      if (recipe) { await baseRecipeService.update(recipe.id, payload); }
      else { await baseRecipeService.create(payload); }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save.');
    } finally { setSaving(false); }
  };

  if (!show) return null;
  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{recipe ? 'Edit Base Recipe' : 'New Base Recipe'}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <div className="mb-3">
              <label className="form-label fw-semibold">Name <span className="text-danger">*</span></label>
              <input className="form-control" value={form.name} onChange={e => setF('name', e.target.value)} placeholder="e.g. Pizza Dough" />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Description</label>
              <textarea className="form-control" rows={2} value={form.description} onChange={e => setF('description', e.target.value)} />
            </div>
            <div className="mb-4">
              <label className="form-label fw-semibold">Total Batch Weight (kg) <span className="text-danger">*</span></label>
              <input className="form-control" type="number" step="0.0001" min="0" value={form.totalBatchWeightKg} onChange={e => setF('totalBatchWeightKg', e.target.value)} placeholder="e.g. 2.500" />
              <div className="form-text">Full batch yield in kg. Used to scale proportionally when assigned to a product.</div>
            </div>
            <div className="mb-2 fw-semibold">Ingredients</div>
            {lines.map((line, idx) => (
              <div key={idx} className="d-flex gap-2 mb-2 align-items-center">
                <div style={{ flex: 3 }}>
                  <SearchableSelect options={ingOptions} value={line.ingredientId}
                    onChange={v => setLine(idx, 'ingredientId', v)} placeholder="Select ingredient…" size="sm" />
                </div>
                <div style={{ flex: 1 }}>
                  <div className="input-group input-group-sm">
                    <input className="form-control" type="number"
                      step={line.unit === 'g' ? '1' : '0.001'} min="0"
                      value={line.displayQty}
                      onChange={e => setLine(idx, 'displayQty', e.target.value)}
                      placeholder="0" />
                    <button type="button" className="btn btn-outline-secondary" style={{ minWidth: 42 }}
                      onClick={() => toggleUnit(idx)} title="Switch unit">
                      {line.unit}
                    </button>
                  </div>
                </div>
                <button className="btn btn-outline-danger btn-sm" onClick={() => removeLine(idx)} disabled={lines.length === 1}>&times;</button>
              </div>
            ))}
            <button className="btn btn-outline-secondary btn-sm mt-1" onClick={addLine}>+ Add ingredient</button>

            <div className={`mt-3 py-2 px-3 rounded ${weightMismatch ? 'alert alert-warning mb-0' : 'text-muted small'}`}>
              {weightMismatch && <span className="fw-semibold">⚠ Ingredients don't add up to the batch weight. </span>}
              Ingredient total: <strong>{ingredientSumKg.toFixed(4)} kg</strong>
              {totalBatchWeightKg > 0 && (
                <> vs. Total Batch Weight <strong>{totalBatchWeightKg.toFixed(4)} kg</strong>
                  {weightMismatch && ` — off by ${weightDiffKg > 0 ? '+' : ''}${weightDiffKg.toFixed(4)} kg.`}
                </>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BaseRecipesSection({ baseRecipes, ingredients, onRefresh }) {
  const [modalShow, setModalShow]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteError, setDeleteError]     = useState('');

  const handleSaved = () => { setModalShow(false); setEditing(null); onRefresh(); };

  const handleDelete = async () => {
    setDeleteError('');
    try {
      await baseRecipeService.remove(confirmDelete.id);
      setConfirmDelete(null);
      onRefresh();
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Delete failed.');
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-header text-white d-flex align-items-center justify-content-between" style={{ backgroundColor: '#1b2638' }}>
        <strong>Base Recipes</strong>
        <button className="btn btn-sm btn-outline-light" onClick={() => { setEditing(null); setModalShow(true); }}>+ New Base Recipe</button>
      </div>
      <div className="card-body">
        <p className="text-muted small mb-3">
          A base recipe (e.g. Pizza Dough) is shared across multiple products by weight. Update it once and all products that reference it are automatically updated in nutrition calculations and label ingredient generation.
        </p>
        {baseRecipes.length === 0 ? (
          <div className="alert alert-light">No base recipes yet.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Batch Weight</th>
                  <th>Ingredients</th>
                  <th>Description</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {baseRecipes.map(r => (
                  <tr key={r.id}>
                    <td className="fw-semibold">{r.name}</td>
                    <td>{Number(r.totalBatchWeightKg).toFixed(4)} kg</td>
                    <td><span className="text-muted small">{(r.Ingredients || []).map(i => i.Ingredient?.name || `#${i.ingredientId}`).join(', ') || '—'}</span></td>
                    <td><span className="text-muted small">{r.description || '—'}</span></td>
                    <td className="text-end">
                      <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => { setEditing(r); setModalShow(true); }}>Edit</button>
                      <button className="btn btn-outline-danger btn-sm" onClick={() => { setDeleteError(''); setConfirmDelete(r); }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <BaseRecipeModal show={modalShow} onClose={() => { setModalShow(false); setEditing(null); }} onSaved={handleSaved} recipe={editing} ingredients={ingredients} />

      {confirmDelete && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header"><h5 className="modal-title">Delete Base Recipe</h5><button className="btn-close" onClick={() => setConfirmDelete(null)} /></div>
              <div className="modal-body">
                {deleteError
                  ? <p className="text-danger">{deleteError}</p>
                  : <p>Delete <strong>{confirmDelete.name}</strong>? This cannot be undone.</p>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { setConfirmDelete(null); setDeleteError(''); }}>Cancel</button>
                {!deleteError && <button className="btn btn-danger" onClick={handleDelete}>Delete</button>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const PAGE_TABS = ['products', 'ingredients', 'base-recipes', 'suppliers'];

function AdminProductPage() {
  const { refresh: refreshPermissions } = usePermissions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [pageTab, setPageTab] = useState(PAGE_TABS.includes(tabFromUrl) ? tabFromUrl : 'products');

  const switchTab = (tab) => {
    setPageTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  const [ingredientHubInitialId, setIngredientHubInitialId] = useState(null);
  const [ingredientHubInitialTab, setIngredientHubInitialTab] = useState(null);

  const navigateToIngredient = (ingredientId) => {
    setShowEditModal(false);
    setIngredientHubInitialId(ingredientId);
    setIngredientHubInitialTab('nutrition');
    switchTab('ingredients');
  };

  const [qboLinked, setQboLinked] = useState({ linkedProductIds: [], linkedItemCodes: {} });
  const [xeroLinked, setXeroLinked] = useState({ linkedProductIds: [], linkedItemCodes: {} });
  const [techSheetByProduct, setTechSheetByProduct] = useState({});
  const [accountingProvider, setAccountingProvider] = useState(null);

  const [productName, setProductName] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [currentPrices, setCurrentPrices] = useState({});
  const [recipeCosts, setRecipeCosts] = useState({});
  const [recipeWeights, setRecipeWeights] = useState({});

  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [newSku, setNewSku] = useState('');
  const [newDefaultUnitsPerCart, setNewDefaultUnitsPerCart] = useState('');
  const [newRecipe, setNewRecipe] = useState([]);
  const [newBaseRecipes, setNewBaseRecipes] = useState([]); // [{baseRecipeId, displayQty, unit}] — unit is 'g' or 'kg', converted to kg on save

  const [editProductId, setEditProductId] = useState(null);
  const [editProductName, setEditProductName] = useState('');
  const [editDefaultUnitsPerCart, setEditDefaultUnitsPerCart] = useState('');
  const [editUnitNetWeightValue, setEditUnitNetWeightValue] = useState('');
  const [editUnitNetWeightUnit, setEditUnitNetWeightUnit] = useState('lb');
  const [editShelfLifeDays, setEditShelfLifeDays] = useState('');
  const [editSku, setEditSku] = useState('');
  const [editGtin, setEditGtin] = useState('');
  const [editNoBake, setEditNoBake] = useState(false);
  const [editIsIntermediate, setEditIsIntermediate] = useState(false);
  const [creatingLinkedIngredient, setCreatingLinkedIngredient] = useState(false);
  const [linkedIngredientStatus, setLinkedIngredientStatus] = useState('');
  const [editRecipe, setEditRecipe] = useState([]);
  const [editBaseRecipes, setEditBaseRecipes] = useState([]); // [{baseRecipeId, displayQty, unit}] — unit is 'g' or 'kg', converted to kg on save
  const [allBaseRecipes, setAllBaseRecipes] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editProductTab, setEditProductTab] = useState('recipe'); // 'recipe' | 'nutrition' | 'packaging' | 'techsheet'
  const [techSheetDirty, setTechSheetDirty] = useState(false);
  const [pendingProductTab, setPendingProductTab] = useState(null);

  // Guard against silently losing an in-progress Tech Sheet draft edit when the user
  // clicks another sub-tab — TechSheetPanel unmounts on tab switch (conditionally
  // rendered), so an unconfirmed switch would discard unsaved changes outright.
  const switchProductTab = (target) => {
    if (editProductTab === 'techsheet' && techSheetDirty && target !== 'techsheet') {
      setPendingProductTab(target);
    } else {
      setEditProductTab(target);
    }
  };

  const [editProductType, setEditProductType] = useState('manufactured'); // 'manufactured' | 'resale'
  const [newProductType, setNewProductType] = useState('manufactured');
  const [editRegulatoryAgency, setEditRegulatoryAgency] = useState('fda'); // 'fda' | 'usda'
  const [newRegulatoryAgency, setNewRegulatoryAgency] = useState('fda');
  const [editProductionFlowType, setEditProductionFlowType] = useState('baking'); // 'baking' | 'pasteurization'
  const [newProductionFlowType, setNewProductionFlowType] = useState('baking');
  const [editWrapperWeight, setEditWrapperWeight] = useState(''); // display value in packagingUnit
  const [packagingUnit, setPackagingUnit] = useState('lb');       // display unit for this session
  // QA reference photo + notes for the AI Visual Inspection SQF checklist field type.
  const [editQaReferenceImageUrl, setEditQaReferenceImageUrl] = useState('');
  const [editQaReferenceNotes, setEditQaReferenceNotes] = useState('');
  const [qaReferenceUploading, setQaReferenceUploading] = useState(false);
  const [qaReferenceDragging, setQaReferenceDragging] = useState(false);
  const qaReferenceFileInputRef = useRef(null);
  const [editPresentations, setEditPresentations] = useState([]);
  const [presEditRow, setPresEditRow] = useState(null); // { id, name, unitsPerRetailBox, retailBoxWeight, masterCaseWeight }
  const [presAddingNew, setPresAddingNew] = useState(false);
  const [presNewRow, setPresNewRow] = useState({ name: '', unitsPerRetailBox: '1', retailBoxWeight: '', masterCaseWeight: '', gtin: '', unitGtin: '' });
  const [cloneFromProductId, setCloneFromProductId] = useState('');

  const [editIngredient, setEditIngredient] = useState(null);
  const [priceIngredient, setPriceIngredient] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [newPrice, setNewPrice] = useState({ pricePerKg: '', effectiveDate: '', note: '', unit: 'kg' });

  // Ingredient details panel (sub-ingredients, allergens, supplier docs)
  const [detailIngredient, setDetailIngredient] = useState(null);
  const [detailSubIngredients, setDetailSubIngredients] = useState([]);
  const [detailAllergenFlags, setDetailAllergenFlags] = useState([]);
  const [ingDocs, setIngDocs] = useState([]);
  const [loadingIngDocs, setLoadingIngDocs] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);

  const [showDeactivated, setShowDeactivated] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // ── Price calculator ──────────────────────────────────────────────────────────
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [calcMode, setCalcMode] = useState('product');
  const [calcProductId, setCalcProductId] = useState('');
  const [calcProductSearch, setCalcProductSearch] = useState('');
  const [calcProductDropdown, setCalcProductDropdown] = useState(false);
  const [calcCustomRecipe, setCalcCustomRecipe] = useState([]);
  const [mfgSellPrice, setMfgSellPrice] = useState('');
  const [mfgGM, setMfgGM] = useState('');
  const [distMarkup, setDistMarkup] = useState('');
  const [distSellPrice, setDistSellPrice] = useState('');
  const [retailMarkup, setRetailMarkup] = useState('');
  const [retailPrice, setRetailPrice] = useState('');
  const [retailMargin, setRetailMargin] = useState('');
  const [calcExtraCost, setCalcExtraCost] = useState('');
  const [calcLaborMode, setCalcLaborMode] = useState('db');
  const [calcLaborDays, setCalcLaborDays] = useState(null);
  const [calcLaborCost, setCalcLaborCost] = useState('');
  const [calcLaborLoading, setCalcLaborLoading] = useState(false);

  useEffect(() => {
    loadProducts();
    loadCompanies();
    loadIngredients();
    loadSuppliers();
    loadCurrentPrices();
    loadRecipeCosts();
    baseRecipeService.list().then(setAllBaseRecipes).catch(() => {});
    qboService.getLinkedProducts().then(setQboLinked).catch(() => {});
    xeroService.getLinkedProducts().then(setXeroLinked).catch(() => {});
    xeroService.getStatus().then(s => { if (s.connected) setAccountingProvider('xero'); }).catch(() => {});
    techSheetService.list({ status: 'all' }).then(sheets => {
      const byProduct = {};
      for (const s of sheets) {
        const existing = byProduct[s.productId];
        // Prefer approved > draft > archived; among same status, higher version wins (already DESC-ordered per product)
        const rank = { approved: 2, draft: 1, archived: 0 };
        if (!existing || rank[s.status] > rank[existing.status]) {
          byProduct[s.productId] = { id: s.id, status: s.status };
        }
      }
      setTechSheetByProduct(byProduct);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProducts = async () => {
    try { setProducts(await productService.getProducts({ includeInactive: true })); }
    catch { showAlert('Error loading products.', 'danger'); }
  };

  const loadCompanies = async () => {
    try { setCompanies(await companyService.getCompanies()); }
    catch { showAlert('Error loading companies.', 'danger'); }
  };

  const loadIngredients = async () => {
    try { setIngredients(await ingredientService.getIngredients()); }
    catch { showAlert('Error loading ingredients.', 'danger'); }
  };

  const loadSuppliers = async () => {
    try { setSuppliers(await supplierService.getSuppliers()); }
    catch { showAlert('Error loading suppliers.', 'danger'); }
  };

  const loadCurrentPrices = async () => {
    try {
      const rows = await ingredientService.getCurrentPrices();
      const map = {};
      for (const r of rows) map[String(r.ingredientId)] = r.pricePerKg;
      setCurrentPrices(map);
    } catch { /* non-critical */ }
  };

  const loadRecipeCosts = async () => {
    try {
      const rows = await productService.getRecipeCosts();
      const costMap = {}, weightMap = {};
      for (const r of rows) {
        costMap[r.productId] = r.estimatedCostPerUnit;
        weightMap[r.productId] = r.totalIngredientWeightKg;
      }
      setRecipeCosts(costMap);
      setRecipeWeights(weightMap);
    } catch { /* non-critical */ }
  };

  // ── Price calculator logic ────────────────────────────────────────────────────
  const calcBaseCost = useMemo(() => {
    if (calcMode === 'product') {
      if (!calcProductId) return null;
      const c = recipeCosts[String(calcProductId)];
      return c != null ? Number(c) : null;
    }
    let total = null;
    for (const r of calcCustomRecipe) {
      const qty = Number(r.expectedQuantityKg);
      const price = currentPrices[String(r.ingredientId)];
      if (qty > 0 && price != null) total = (total ?? 0) + qty * price;
    }
    return total;
  }, [calcMode, calcProductId, recipeCosts, calcCustomRecipe, currentPrices]);

  const calcTotalCost = calcBaseCost != null
    ? calcBaseCost + (Number(calcExtraCost) || 0) + (Number(calcLaborCost) || 0)
    : null;

  const fetchLaborCost = async (days) => {
    setCalcLaborDays(days);
    setCalcLaborLoading(true);
    setCalcLaborCost('');
    calcResetPricing();
    try {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - days);
      const fmt = d => d.toISOString().slice(0, 10);
      const params = { startDate: fmt(start), endDate: fmt(end) };
      const data = await analyticsService.getProductionSummary(params);
      const val = data?.kpis?.laborCostPerUnit;
      setCalcLaborCost(val != null ? String(Number(val).toFixed(4)) : '');
    } catch { setCalcLaborCost(''); }
    finally { setCalcLaborLoading(false); }
  };

  const calcResetPricing = () => {
    setMfgSellPrice(''); setMfgGM('');
    setDistMarkup(''); setDistSellPrice('');
    setRetailMarkup(''); setRetailPrice(''); setRetailMargin('');
  };

  // Retailer profit margin, as retailers compute it (not markup): (sell price - wholesale cost) / sell price × 100
  const calcRetailMarginFromPrice = (price, base) =>
    price > 0 && base > 0 ? (((price - base) / price) * 100).toFixed(1) : '';

  const calcHandleMfgPrice = (val) => {
    setMfgSellPrice(val);
    const p = Number(val);
    const cost = calcTotalCost;
    if (p > 0 && cost != null && cost > 0) {
      setMfgGM(((p - cost) / p * 100).toFixed(1));
    } else { setMfgGM(''); }
    // cascade downstream
    const dm = Number(distMarkup);
    if (p > 0 && dm > 0) {
      const dp = p * (1 + dm / 100);
      setDistSellPrice(dp.toFixed(4));
      const rm = Number(retailMarkup);
      if (rm > 0) {
        const rp = dp * (1 + rm / 100);
        setRetailPrice(rp.toFixed(4));
        setRetailMargin(calcRetailMarginFromPrice(rp, dp));
      }
    } else if (p > 0) {
      const rm = Number(retailMarkup);
      if (rm > 0) {
        const rp = p * (1 + rm / 100);
        setRetailPrice(rp.toFixed(4));
        setRetailMargin(calcRetailMarginFromPrice(rp, p));
      }
    }
  };

  const calcHandleMfgGM = (val) => {
    setMfgGM(val);
    const gm = Number(val);
    const cost = calcTotalCost;
    if (gm >= 0 && gm < 100 && cost != null && cost > 0) {
      const p = cost / (1 - gm / 100);
      setMfgSellPrice(p.toFixed(4));
      const dm = Number(distMarkup);
      if (dm > 0) {
        const dp = p * (1 + dm / 100);
        setDistSellPrice(dp.toFixed(4));
        const rm = Number(retailMarkup);
        if (rm > 0) {
          const rp = dp * (1 + rm / 100);
          setRetailPrice(rp.toFixed(4));
          setRetailMargin(calcRetailMarginFromPrice(rp, dp));
        }
      } else {
        const rm = Number(retailMarkup);
        if (rm > 0) {
          const rp = p * (1 + rm / 100);
          setRetailPrice(rp.toFixed(4));
          setRetailMargin(calcRetailMarginFromPrice(rp, p));
        }
      }
    } else { setMfgSellPrice(''); }
  };

  const calcHandleDistMarkup = (val) => {
    setDistMarkup(val);
    const mfgNum = Number(mfgSellPrice);
    if (mfgNum > 0 && val !== '') {
      const dp = mfgNum * (1 + Number(val) / 100);
      setDistSellPrice(dp.toFixed(4));
      const rm = Number(retailMarkup);
      if (rm > 0) {
        const rp = dp * (1 + rm / 100);
        setRetailPrice(rp.toFixed(4));
        setRetailMargin(calcRetailMarginFromPrice(rp, dp));
      }
    } else {
      setDistSellPrice('');
      const rm = Number(retailMarkup);
      if (mfgNum > 0 && rm > 0) {
        const rp = mfgNum * (1 + rm / 100);
        setRetailPrice(rp.toFixed(4));
        setRetailMargin(calcRetailMarginFromPrice(rp, mfgNum));
      }
    }
  };

  const calcHandleDistPrice = (val) => {
    setDistSellPrice(val);
    const mfgNum = Number(mfgSellPrice);
    if (mfgNum > 0 && val !== '') {
      const p = Number(val);
      setDistMarkup(p > mfgNum ? ((p - mfgNum) / mfgNum * 100).toFixed(1) : '');
      const rm = Number(retailMarkup);
      if (p > 0 && rm > 0) {
        const rp = p * (1 + rm / 100);
        setRetailPrice(rp.toFixed(4));
        setRetailMargin(calcRetailMarginFromPrice(rp, p));
      }
    } else { setDistMarkup(''); }
  };

  const calcHandleRetailMarkup = (val) => {
    setRetailMarkup(val);
    const distNum = Number(distSellPrice);
    const mfgNum = Number(mfgSellPrice);
    const base = distNum > 0 ? distNum : mfgNum;
    if (base > 0 && val !== '') {
      const rp = base * (1 + Number(val) / 100);
      setRetailPrice(rp.toFixed(4));
      setRetailMargin(calcRetailMarginFromPrice(rp, base));
    } else { setRetailPrice(''); setRetailMargin(''); }
  };

  const calcHandleRetailPrice = (val) => {
    setRetailPrice(val);
    const distNum = Number(distSellPrice);
    const mfgNum = Number(mfgSellPrice);
    const base = distNum > 0 ? distNum : mfgNum;
    if (base > 0 && val !== '') {
      const p = Number(val);
      setRetailMarkup(p > 0 ? ((p - base) / base * 100).toFixed(1) : '');
      setRetailMargin(calcRetailMarginFromPrice(p, base));
    } else { setRetailMarkup(''); setRetailMargin(''); }
  };

  const calcHandleRetailMargin = (val) => {
    setRetailMargin(val);
    const margin = Number(val);
    const distNum = Number(distSellPrice);
    const mfgNum = Number(mfgSellPrice);
    const base = distNum > 0 ? distNum : mfgNum;
    if (base > 0 && val !== '' && margin >= 0 && margin < 100) {
      const rp = base / (1 - margin / 100);
      setRetailPrice(rp.toFixed(4));
      setRetailMarkup(((rp - base) / base * 100).toFixed(1));
    } else { setRetailPrice(''); setRetailMarkup(''); }
  };

  const showAlert = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => { setMessage(''); setMessageType(''); }, 4000);
  };

  const normalizeRecipe = (raw) =>
    raw.map(item => ({
      ingredientId: item.ingredientId ?? item,
      expectedQuantityKg: item.expectedQuantityKg ?? '',
    }));

  const prepareRecipe = (recipe) =>
    recipe.map(r => ({
      ingredientId: Number(r.ingredientId),
      expectedQuantityKg: r.expectedQuantityKg !== '' ? Number(r.expectedQuantityKg) : null,
    }));

  const handleToggleRequireQty = async () => {
    const company = companies[0];
    if (!company) return;
    const newValue = !company.requireIngredientQty;
    try {
      const updated = await companyService.updateSettings({ requireIngredientQty: newValue });
      setCompanies([updated]);
      refreshPermissions();
    } catch { showAlert('Error updating setting.', 'danger'); }
  };


  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const baseRecipesPayload = newBaseRecipes
        .filter(r => r.baseRecipeId && Number(r.displayQty) > 0)
        .map((r, idx) => ({ baseRecipeId: Number(r.baseRecipeId), quantityKg: r.unit === 'g' ? Number(r.displayQty) / 1000 : Number(r.displayQty), sortOrder: idx }));
      await productService.createProduct(productName, null, prepareRecipe(newRecipe), newDefaultUnitsPerCart, newSku, newProductType, { baseRecipes: baseRecipesPayload, regulatoryAgency: newRegulatoryAgency, productionFlowType: newProductionFlowType });
      showAlert('Product created successfully!', 'success');
      setProductName('');
      setNewSku('');
      setNewDefaultUnitsPerCart('');
      setNewRecipe([]);
      setNewBaseRecipes([]);
      setNewProductType('manufactured');
      setNewRegulatoryAgency('fda');
      setNewProductionFlowType('baking');
      setShowCreateProduct(false);
      loadProducts();
      loadRecipeCosts();
    } catch { showAlert('Error creating product.', 'danger'); }
  };

  const startEdit = async (product, openTab = 'recipe') => {
    setEditProductId(product.id);
    setEditProductName(product.name);
    setEditDefaultUnitsPerCart(product.defaultUnitsPerCart != null ? String(product.defaultUnitsPerCart) : '');
    setEditUnitNetWeightValue(product.unitNetWeightValue != null ? String(product.unitNetWeightValue) : '');
    setEditUnitNetWeightUnit(product.unitNetWeightUnit || 'lb');
    setEditShelfLifeDays(product.shelfLifeDays != null ? String(product.shelfLifeDays) : '');
    setEditSku(product.sku || '');
    setEditGtin(product.gtin || '');
    setEditNoBake(Boolean(product.noBake));
    setEditIsIntermediate(Boolean(product.isIntermediate));
    setLinkedIngredientStatus('');
    const pType = product.productType || 'manufactured';
    setEditProductType(pType);
    setEditRegulatoryAgency(product.regulatoryAgency || 'fda');
    setEditProductionFlowType(product.productionFlowType || 'baking');
    if (pType === 'resale' && (openTab === 'recipe' || openTab === 'nutrition')) openTab = 'packaging';
    const initUnit = 'lb';
    setPackagingUnit(initUnit);
    const kgToDisplay = (kg) => kg != null ? String(Math.round(Number(kg) * 2.20462 * 100000) / 100000) : '';
    setEditWrapperWeight(initUnit === 'lb' ? kgToDisplay(product.wrapperWeightKg) : (product.wrapperWeightKg != null ? String(product.wrapperWeightKg) : ''));
    setEditQaReferenceImageUrl(product.qaReferenceImageUrl || '');
    setEditQaReferenceNotes(product.qaReferenceNotes || '');
    setEditPresentations(product.Presentations || []);
    setPresEditRow(null);
    setPresAddingNew(false);
    setPresNewRow({ name: '', unitsPerRetailBox: '1', retailBoxWeight: '', masterCaseWeight: '', gtin: '' });
    setCloneFromProductId('');
    try {
      const raw = await productService.getProductRecipe(product.id);
      const directIngredients = Array.isArray(raw) ? raw : (raw.ingredients || []);
      const baseRefs = Array.isArray(raw) ? [] : (raw.baseRecipes || []);
      setEditRecipe(normalizeRecipe(directIngredients));
      setEditBaseRecipes(baseRefs.map(r => ({ baseRecipeId: String(r.baseRecipeId), displayQty: r.quantityKg != null ? String(parseFloat((Number(r.quantityKg) * 1000).toFixed(4))) : '', unit: 'g' })));
    } catch { setEditRecipe([]); setEditBaseRecipes([]); }
    setEditProductTab(openTab);
    setShowEditModal(true);
  };

  const saveEdit = async () => {
    try {
      await productService.updateProduct(editProductId, editProductName, null, prepareRecipe(editRecipe), editDefaultUnitsPerCart, {
        unitNetWeightValue: editUnitNetWeightValue !== '' ? Number(editUnitNetWeightValue) : null,
        unitNetWeightUnit: editUnitNetWeightUnit,
        shelfLifeDays: editShelfLifeDays !== '' ? Number(editShelfLifeDays) : null,
        sku: editSku !== '' ? editSku : null,
        wrapperWeightKg: editWrapperWeight !== ''
          ? (packagingUnit === 'lb' ? Math.round(Number(editWrapperWeight) * 0.453592 * 100000) / 100000 : Number(editWrapperWeight))
          : null,
        noBake: editNoBake,
        gtin: editGtin !== '' ? editGtin : null,
        productType: editProductType,
        regulatoryAgency: editRegulatoryAgency,
        productionFlowType: editProductionFlowType,
        isIntermediate: editIsIntermediate,
        qaReferenceImageUrl: editQaReferenceImageUrl !== '' ? editQaReferenceImageUrl : null,
        qaReferenceNotes: editQaReferenceNotes !== '' ? editQaReferenceNotes : null,
        baseRecipes: editBaseRecipes
          .filter(r => r.baseRecipeId && Number(r.displayQty) > 0)
          .map((r, idx) => ({ baseRecipeId: Number(r.baseRecipeId), quantityKg: r.unit === 'g' ? Number(r.displayQty) / 1000 : Number(r.displayQty), sortOrder: idx })),
      });
      showAlert('Product updated successfully!', 'success');
      loadProducts();
      loadRecipeCosts();
    } catch { showAlert('Error updating product.', 'danger'); }
  };

  // QA reference photo for the AI Visual Inspection SQF checklist field type — uploaded
  // immediately on file pick (not deferred to Save), matching this app's standard
  // presigned-PUT-then-store-the-URL upload convention used elsewhere (e.g. Tech Sheet images).
  const uploadQaReferenceFile = async (file) => {
    if (!file || !editProductId) return;
    if (!file.type?.startsWith('image/')) { showAlert('Please drop an image file.', 'danger'); return; }
    setQaReferenceUploading(true);
    try {
      const uploadFile = await compressImage(file).catch(() => file);
      const { uploadUrl, publicUrl } = await productService.getQaReferenceUploadUrl(editProductId, uploadFile.name, uploadFile.type, uploadFile.size);
      await axios.put(uploadUrl, uploadFile, { headers: { 'Content-Type': uploadFile.type } });
      setEditQaReferenceImageUrl(publicUrl);
    } catch {
      showAlert('Failed to upload reference photo.', 'danger');
    } finally {
      setQaReferenceUploading(false);
    }
  };

  const handleQaReferenceFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    uploadQaReferenceFile(file);
  };

  const handleQaReferenceDrop = (e) => {
    e.preventDefault();
    setQaReferenceDragging(false);
    uploadQaReferenceFile(e.dataTransfer.files?.[0]);
  };

  const toggleBrUnit = (setter, idx) => setter(rs => rs.map((r, i) => {
    if (i !== idx) return r;
    const val = parseFloat(r.displayQty);
    if (r.unit === 'kg') return { ...r, unit: 'g', displayQty: isNaN(val) ? '' : String(parseFloat((val * 1000).toFixed(4))) };
    return { ...r, unit: 'kg', displayQty: isNaN(val) ? '' : String(parseFloat((val / 1000).toFixed(4))) };
  }));

  const toKg = (val) => {
    if (val === '' || val == null) return null;
    const n = Number(val);
    if (isNaN(n) || n === 0) return n === 0 ? 0 : null;
    return packagingUnit === 'lb' ? Math.round(n * 0.453592 * 100000) / 100000 : n;
  };

  const fromKg = (kg) => {
    if (kg == null) return '';
    const n = Number(kg);
    return packagingUnit === 'lb'
      ? String(Math.round(n * 2.20462 * 100000) / 100000)
      : String(n);
  };

  const togglePackagingUnit = (unit) => {
    if (unit === packagingUnit) return;
    const conv = (v) => {
      if (v === '' || v == null) return '';
      const n = Number(v);
      if (isNaN(n)) return v;
      return unit === 'kg'
        ? String(Math.round(n * 0.453592 * 100000) / 100000)
        : String(Math.round(n * 2.20462 * 100000) / 100000);
    };
    setEditWrapperWeight(w => conv(w));
    setPresNewRow(r => ({ ...r, retailBoxWeight: conv(r.retailBoxWeight), masterCaseWeight: conv(r.masterCaseWeight) }));
    setPresEditRow(r => r ? { ...r, retailBoxWeight: conv(r.retailBoxWeight), masterCaseWeight: conv(r.masterCaseWeight) } : r);
    setPackagingUnit(unit);
  };

  const addPresentation = async () => {
    try {
      const created = await productService.createPresentation(editProductId, {
        name: presNewRow.name,
        unitsPerRetailBox: presNewRow.unitsPerRetailBox || 1,
        retailBoxWeightKg: toKg(presNewRow.retailBoxWeight),
        masterCaseWeightKg: toKg(presNewRow.masterCaseWeight),
        gtin: presNewRow.gtin || null,
        unitGtin: presNewRow.unitGtin || null,
      });
      setEditPresentations(prev => [...prev, created]);
      setPresNewRow({ name: '', unitsPerRetailBox: '1', retailBoxWeight: '', masterCaseWeight: '', gtin: '', unitGtin: '' });
      setPresAddingNew(false);
    } catch { showAlert('Error adding presentation.', 'danger'); }
  };

  const savePresentation = async (pres) => {
    try {
      const updated = await productService.updatePresentation(editProductId, pres.id, {
        name: pres.name,
        unitsPerRetailBox: pres.unitsPerRetailBox || 1,
        retailBoxWeightKg: toKg(pres.retailBoxWeight),
        masterCaseWeightKg: toKg(pres.masterCaseWeight),
        gtin: pres.gtin || null,
        unitGtin: pres.unitGtin || null,
      });
      setEditPresentations(prev => prev.map(p => p.id === pres.id ? updated : p));
      setPresEditRow(null);
    } catch { showAlert('Error updating presentation.', 'danger'); }
  };

  const deletePresentation = async (presId) => {
    try {
      await productService.deletePresentation(editProductId, presId);
      setEditPresentations(prev => prev.filter(p => p.id !== presId));
    } catch { showAlert('Error deleting presentation.', 'danger'); }
  };

  const clonePresentationsFrom = async () => {
    if (!cloneFromProductId) return;
    try {
      const created = await productService.clonePresentations(editProductId, cloneFromProductId);
      setEditPresentations(created);
      setCloneFromProductId('');
    } catch { showAlert('Error cloning presentations.', 'danger'); }
  };

  const handleToggleActive = async (product) => {
    try {
      const { isActive } = await productService.toggleActive(product.id);
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, isActive } : p));
      showAlert(`Product ${isActive ? 'reactivated' : 'deactivated'}.`, 'success');
    } catch { showAlert('Error updating product status.', 'danger'); }
  };

  const handleClone = async (product) => {
    try {
      await productService.cloneProduct(product.id);
      showAlert(`"${product.name}" cloned successfully. Edit the copy to rename it.`, 'success');
      loadProducts();
      loadRecipeCosts();
    } catch { showAlert('Error cloning product.', 'danger'); }
  };

  const activeProducts = products.filter(p => p.isActive);
  const inactiveProducts = products.filter(p => !p.isActive);
  const filteredProducts = productSearch.trim()
    ? activeProducts.filter(p => {
        const q = productSearch.toLowerCase();
        return p.name.toLowerCase().includes(q) || (p.sku && p.sku.toLowerCase().includes(q));
      })
    : activeProducts;
  const filteredIngredients = ingredientSearch.trim()
    ? ingredients.filter(i =>
        i.name.toLowerCase().includes(ingredientSearch.toLowerCase()) ||
        (i.manufacturer || '').toLowerCase().includes(ingredientSearch.toLowerCase()) ||
        (i.category || '').toLowerCase().includes(ingredientSearch.toLowerCase()) ||
        (i.rawMaterialCode || '').toLowerCase().includes(ingredientSearch.toLowerCase())
      )
    : ingredients;

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDragLeave = () => setDragOverIdx(null);
  const handleDrop = async (e, dropIdx) => {
    e.preventDefault();
    setDragOverIdx(null);
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); return; }
    const reordered = [...activeProducts];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setDragIdx(null);
    setProducts([...reordered, ...inactiveProducts]);
    try {
      await productService.reorderProducts(reordered.map((p, i) => ({ id: p.id, sortOrder: i })));
    } catch {
      showAlert('Error saving order.', 'danger');
      loadProducts();
    }
  };

  const handleIngredientUpdate = async (id) => {
    try {
      await ingredientService.updateIngredient(id, {
        name: editIngredient.name,
        category: editIngredient.category || null,
        rawMaterialCode: editIngredient.rawMaterialCode || null,
        caseSize: editIngredient.caseSize !== '' && editIngredient.caseSize != null ? Number(editIngredient.caseSize) : null,
        caseSizeUnit: editIngredient.caseSizeUnit || 'kg',
        caseLabel: editIngredient.caseLabel || null,
      });
      showAlert('Ingredient updated successfully!', 'success');
      setEditIngredient(null);
      loadIngredients();
    } catch { showAlert('Error updating ingredient.', 'danger'); }
  };

  const startEditIngredient = (ing) => {
    setEditIngredient(ing);
  };

  const startViewPrices = async (ing) => {
    setPriceIngredient(ing);
    setNewPrice({ pricePerKg: '', effectiveDate: '', note: '', unit: 'kg' });
    setLoadingPrices(true);
    try {
      setPriceHistory(await ingredientService.getPrices(ing.id));
    } catch { setPriceHistory([]); }
    finally { setLoadingPrices(false); }
  };

  const handleAddPrice = async () => {
    if (!newPrice.pricePerKg || !newPrice.effectiveDate) {
      showAlert('Price and effective date are required.', 'danger');
      return;
    }
    const LB_TO_KG = 0.453592;
    const pricePerKg = newPrice.unit === 'lb'
      ? Number(newPrice.pricePerKg) / LB_TO_KG
      : Number(newPrice.pricePerKg);
    try {
      const entry = await ingredientService.addPrice(priceIngredient.id, {
        pricePerKg,
        effectiveDate: newPrice.effectiveDate,
        note: newPrice.note,
      });
      setPriceHistory(prev => [entry, ...prev]);
      setNewPrice({ pricePerKg: '', effectiveDate: '', note: '', unit: newPrice.unit });
      showAlert('Price recorded.', 'success');
      loadCurrentPrices();
      loadRecipeCosts();
    } catch { showAlert('Error recording price.', 'danger'); }
  };

  const ALLERGENS = [
    { key: 'milk',      label: 'Milk' },
    { key: 'eggs',      label: 'Eggs' },
    { key: 'fish',      label: 'Fish' },
    { key: 'shellfish', label: 'Shellfish' },
    { key: 'tree_nuts', label: 'Tree Nuts' },
    { key: 'wheat',     label: 'Wheat' },
    { key: 'peanuts',   label: 'Peanuts' },
    { key: 'soybeans',  label: 'Soybeans' },
    { key: 'sesame',    label: 'Sesame' },
  ];

  const startViewDetails = async (ing) => {
    setPriceIngredient(null);
    setPriceHistory([]);
    setDetailIngredient(ing);
    setDetailSubIngredients(ing.subIngredients || []);
    setDetailAllergenFlags(ing.allergenFlags || []);
    setLoadingIngDocs(true);
    try {
      setIngDocs(await documentService.getDocumentsForIngredient(ing.id));
    } catch { setIngDocs([]); }
    finally { setLoadingIngDocs(false); }
  };

  const saveIngredientDetails = async () => {
    if (!detailIngredient) return;
    setSavingDetails(true);
    try {
      await ingredientService.updateIngredient(detailIngredient.id, {
        subIngredients: detailSubIngredients,
        allergenFlags: detailAllergenFlags,
      });
      showAlert('Ingredient details saved.', 'success');
      loadIngredients();
    } catch { showAlert('Error saving ingredient details.', 'danger'); }
    finally { setSavingDetails(false); }
  };

  const addSubIngredient = () =>
    setDetailSubIngredients(prev => [...prev, { name: '', allergens: [] }]);

  const updateSubIngredient = (idx, field, value) =>
    setDetailSubIngredients(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));

  const removeSubIngredient = (idx) =>
    setDetailSubIngredients(prev => prev.filter((_, i) => i !== idx));

  const toggleAllergen = (key) =>
    setDetailAllergenFlags(prev =>
      prev.includes(key) ? prev.filter(a => a !== key) : [...prev, key]
    );

  const cardHeader = (text) => (
    <div className="card-header text-white d-flex align-items-center" style={{ backgroundColor: '#1b2638' }}>
      <strong>{text}</strong>
    </div>
  );

  const fmtCost = (productId) => {
    const cost = recipeCosts[productId];
    if (cost == null) return <span className="text-muted">—</span>;
    return <span className="badge text-bg-light border">${Number(cost).toFixed(4)}</span>;
  };

  const fmtWeight = (productId) => {
    const w = recipeWeights[productId];
    if (w == null) return <span className="text-muted">—</span>;
    return <span>{Number(w).toFixed(3)} kg</span>;
  };

  return (
    <div className="container mt-4 pb-5">
      {message && (
        <div className={`alert alert-${messageType} alert-dismissible fade show fixed-top w-50 mx-auto mt-3`} role="alert" style={{ zIndex: 1100 }}>
          {message}
          <button type="button" className="btn-close" onClick={() => setMessage('')} />
        </div>
      )}

      {/* ── Page-level tabs ── */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${pageTab === 'products' ? 'active' : ''}`} onClick={() => switchTab('products')}>
            Products
            {products.filter(p => p.isActive).length > 0 && (
              <span className="badge bg-secondary ms-2" style={{ fontSize: 11 }}>{products.filter(p => p.isActive).length}</span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${pageTab === 'ingredients' ? 'active' : ''}`} onClick={() => switchTab('ingredients')}>
            Ingredients
            {ingredients.length > 0 && (
              <span className="badge bg-secondary ms-2" style={{ fontSize: 11 }}>{ingredients.length}</span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${pageTab === 'base-recipes' ? 'active' : ''}`} onClick={() => switchTab('base-recipes')}>
            Base Recipes
            {allBaseRecipes.length > 0 && (
              <span className="badge bg-secondary ms-2" style={{ fontSize: 11 }}>{allBaseRecipes.length}</span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${pageTab === 'suppliers' ? 'active' : ''}`} onClick={() => switchTab('suppliers')}>
            Suppliers
            {suppliers.length > 0 && (
              <span className="badge bg-secondary ms-2" style={{ fontSize: 11 }}>{suppliers.length}</span>
            )}
          </button>
        </li>
      </ul>

      {/* ── Company Settings bar (products tab only) ── */}
      {pageTab === 'products' && companies[0] && (
        <div className="d-flex align-items-center gap-3 mb-3 px-1 flex-wrap">
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="toggleRequireQty"
              checked={!!companies[0].requireIngredientQty}
              onChange={handleToggleRequireQty}
            />
            <label className="form-check-label" htmlFor="toggleRequireQty" style={{ fontSize: 14 }}>
              Require ingredient quantities in Add Log
            </label>
          </div>
          <span className="badge" style={{ fontSize: 12, background: companies[0].requireIngredientQty ? '#198754' : '#6c757d' }}>
            {companies[0].requireIngredientQty ? 'On' : 'Off'}
          </span>
        </div>
      )}

      {/* ── Products card ── */}
      {pageTab === 'products' && <div className="card mb-4">
        {cardHeader('Products')}
        <div className="card-body">

          {/* Action buttons */}
          <div className="mb-3 d-flex gap-2 flex-wrap">
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => setShowCreateProduct(v => !v)}
            >
              {showCreateProduct ? '− Cancel' : '+ New Product'}
            </button>
            {inactiveProducts.length > 0 && (
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowDeactivated(v => !v)}
              >
                {showDeactivated ? 'Hide Deactivated' : `Deactivated (${inactiveProducts.length})`}
              </button>
            )}
            {accountingProvider === 'xero' ? (
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => navigate('/admin/xero/sync?tab=items')}
              >
                Sync with Xero
              </button>
            ) : (
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => navigate('/admin/qbo/sync?tab=products')}
              >
                Sync with QBO
              </button>
            )}
            <Link to="/admin/gs1-import" className="btn btn-outline-secondary btn-sm">
              GS1 Import
            </Link>
            <button
              className="btn btn-outline-info btn-sm ms-auto"
              onClick={() => setShowCalcModal(true)}
            >
              $ Price Calculator
            </button>
          </div>

          {showCreateProduct && (
            <form onSubmit={handleProductSubmit} className="mb-4 p-3 border rounded bg-light">
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label">Product Name</label>
                  <input type="text" className="form-control" value={productName} onChange={e => setProductName(e.target.value)} required />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Type</label>
                  <select className="form-select" value={newProductType} onChange={e => setNewProductType(e.target.value)}>
                    <option value="manufactured">Manufactured</option>
                    <option value="resale">Resale / Service</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">SKU <small className="text-muted">(optional)</small></label>
                  <input
                    type="text" className="form-control"
                    placeholder="e.g. CHZPZ-001"
                    value={newSku}
                    onChange={e => setNewSku(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">
                    Regulated By
                    <small className="text-muted"> — inspection agency</small>
                  </label>
                  <select className="form-select" value={newRegulatoryAgency} onChange={e => setNewRegulatoryAgency(e.target.value)}>
                    <option value="fda">FDA</option>
                    <option value="usda">USDA (meat/poultry)</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">
                    Production Process
                    <small className="text-muted"> — CCP flow</small>
                  </label>
                  <select className="form-select" value={newProductionFlowType} onChange={e => setNewProductionFlowType(e.target.value)}>
                    <option value="baking">Baking</option>
                    <option value="pasteurization">Pasteurization</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Default units / cart <small className="text-muted">(optional)</small></label>
                  <input
                    type="number" min="1" step="1" className="form-control"
                    placeholder="e.g. 12"
                    value={newDefaultUnitsPerCart}
                    onChange={e => setNewDefaultUnitsPerCart(e.target.value)}
                  />
                </div>
              </div>
              {newProductType === 'manufactured' && (
                <>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label mb-0 fw-semibold">
                        Base Recipes <small className="text-muted fw-normal">— shared sub-recipes (e.g. dough)</small>
                      </label>
                      <button type="button" className="btn btn-outline-secondary btn-sm"
                        onClick={() => setNewBaseRecipes(r => [...r, { baseRecipeId: '', displayQty: '', unit: 'g' }])}>
                        + Add
                      </button>
                    </div>
                    {newBaseRecipes.length === 0 ? (
                      <div className="text-muted small">No base recipes assigned.</div>
                    ) : (
                      newBaseRecipes.map((ref, idx) => {
                        const brCost = computeBaseRecipeCostPerUnit(ref, allBaseRecipes, currentPrices);
                        return (
                          <div key={idx} className="d-flex gap-2 mb-2 align-items-center">
                            <div style={{ flex: 3 }}>
                              <SearchableSelect
                                options={allBaseRecipes.map(br => ({ value: String(br.id), label: br.name }))}
                                value={ref.baseRecipeId}
                                onChange={v => setNewBaseRecipes(rs => rs.map((r, i) => i === idx ? { ...r, baseRecipeId: v } : r))}
                                placeholder="Select base recipe…"
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div className="input-group">
                                <input type="number" className="form-control" min="0"
                                  step={ref.unit === 'g' ? '1' : '0.001'} placeholder="0"
                                  value={ref.displayQty}
                                  onChange={e => setNewBaseRecipes(rs => rs.map((r, i) => i === idx ? { ...r, displayQty: e.target.value } : r))} />
                                <button type="button" className="btn btn-outline-secondary" style={{ minWidth: 42 }}
                                  onClick={() => toggleBrUnit(setNewBaseRecipes, idx)} title="Switch unit">
                                  {ref.unit}
                                </button>
                                {brCost != null && (
                                  <span className="input-group-text text-success fw-semibold" style={{ fontSize: 13 }}>
                                    ${brCost.toFixed(4)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <button type="button" className="btn btn-outline-danger"
                              onClick={() => setNewBaseRecipes(rs => rs.filter((_, i) => i !== idx))} title="Remove">&times;</button>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="mb-3 pt-3" style={{ borderTop: '1px solid #dee2e6' }}>
                    <label className="form-label">
                      Direct Ingredients <small className="text-muted">— check ingredients and enter the expected quantity per unit produced</small>
                    </label>
                    <RecipeEditor
                      ingredients={ingredients}
                      recipeItems={newRecipe}
                      onChange={setNewRecipe}
                      priceMap={currentPrices}
                      baseRecipeCost={newBaseRecipes.reduce((acc, ref) => {
                        const c = computeBaseRecipeCostPerUnit(ref, allBaseRecipes, currentPrices);
                        return c != null ? (acc || 0) + c : acc;
                      }, null)}
                    />
                  </div>
                </>
              )}
              <button type="submit" className="btn btn-primary btn-sm">Create Product</button>
            </form>
          )}

          {/* Active products table (draggable) */}
          <input
            type="text"
            className="form-control form-control-sm mb-2"
            placeholder="Search products…"
            value={productSearch}
            onChange={e => setProductSearch(e.target.value)}
          />
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-sm table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 32 }} title="Drag to reorder"></th>
                  <th style={{ width: 46 }} className="text-center" title="Regulatory agency">Agency</th>
                  <th style={{ width: 40 }} className="text-center" title="Tech sheet">📄</th>
                  <th>Product Name</th>
                  <th style={{ width: 120 }}>SKU</th>
                  <th className="text-end" style={{ width: 130 }}>Ingr. Weight</th>
                  <th className="text-end" style={{ width: 140 }}>Est. Cost / unit</th>
                  <th style={{ width: 100 }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 && (
                  <tr><td colSpan={8} className="text-muted text-center">{productSearch ? 'No products match your search.' : 'No active products yet.'}</td></tr>
                )}
                {filteredProducts.map((product, idx) => (
                  <tr
                    key={product.id}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, idx)}
                    style={{
                      background: dragOverIdx === idx ? '#e8f4ff' : undefined,
                      outline: dragOverIdx === idx ? '2px dashed #2f80ed' : undefined,
                      cursor: 'grab',
                    }}
                  >
                    <td className="text-center text-muted" style={{ fontSize: 16, userSelect: 'none' }}>☰</td>
                    <td className="text-center">
                      {product.regulatoryAgency === 'usda' ? (
                        <span
                          className="badge rounded-pill"
                          style={{ backgroundColor: '#7a1f2b', color: '#fff', fontSize: 9, letterSpacing: 0.5 }}
                          title="USDA/FSIS regulated — mark of inspection required on label"
                        >
                          USDA
                        </span>
                      ) : (
                        <span
                          className="badge rounded-pill bg-light text-secondary border"
                          style={{ fontSize: 9, letterSpacing: 0.5 }}
                          title="FDA regulated"
                        >
                          FDA
                        </span>
                      )}
                    </td>
                    <td className="text-center">
                      {techSheetByProduct[product.id] ? (
                        <a
                          href={`/techsheets/${techSheetByProduct[product.id].id}/preview`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={techSheetByProduct[product.id].status === 'draft' ? 'Tech sheet — draft in progress' : 'Tech sheet — view / print'}
                          style={{ textDecoration: 'none', opacity: techSheetByProduct[product.id].status === 'draft' ? 0.5 : 1 }}
                        >
                          📄
                        </a>
                      ) : (
                        <span className="text-muted" style={{ opacity: 0.25 }} title="No tech sheet yet">📄</span>
                      )}
                    </td>
                    <td>
                      {product.name}
                      {product.productType === 'resale' && (
                        <span className="badge bg-info text-dark ms-2" style={{ fontSize: 9, verticalAlign: 'middle' }}>Resale</span>
                      )}
                      {qboLinked.linkedProductIds.includes(product.id) && (
                        <span
                          className="badge bg-success ms-2"
                          style={{ fontSize: 9, verticalAlign: 'middle', opacity: 0.85 }}
                          title={`QBO: ${qboLinked.linkedItemCodes[product.id]}`}
                        >
                          QB
                        </span>
                      )}
                      {xeroLinked.linkedProductIds.includes(product.id) && (
                        <span
                          className="badge ms-2"
                          style={{ fontSize: 9, verticalAlign: 'middle', opacity: 0.85, backgroundColor: '#13b5ea' }}
                          title={`Xero: ${xeroLinked.linkedItemCodes[product.id]}`}
                        >
                          Xero
                        </span>
                      )}
                    </td>
                    <td className="text-muted small">{product.sku || '—'}</td>
                    <td className="text-end">{fmtWeight(product.id)}</td>
                    <td className="text-end">{fmtCost(product.id)}</td>
                    <td className="text-center">
                      <div className="d-flex gap-1 justify-content-center">
                        <button className="btn btn-outline-primary btn-sm" onClick={() => startEdit(product, 'nutrition')}>🧪</button>
                        <button className="btn btn-outline-primary btn-sm" onClick={() => startEdit(product)}>Edit</button>
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => handleClone(product)}>Clone</button>
                        <button className="btn btn-outline-warning btn-sm" onClick={() => handleToggleActive(product)}>Deactivate</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Deactivated products (collapsible) */}
          {showDeactivated && inactiveProducts.length > 0 && (
            <div className="mt-3">
              <p className="text-muted mb-2" style={{ fontSize: 13 }}>Deactivated products are hidden from all dropdowns and selectors.</p>
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Product Name</th>
                      <th className="text-end" style={{ width: 140 }}>Est. Cost / unit</th>
                      <th style={{ width: 100 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {inactiveProducts.map(product => (
                      <tr key={product.id} style={{ opacity: 0.6 }}>
                        <td>{product.name}</td>
                        <td className="text-end">{fmtCost(product.id)}</td>
                        <td className="text-center">
                          <button className="btn btn-outline-success btn-sm" onClick={() => handleToggleActive(product)}>Reactivate</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>}

      {/* ── Ingredients tab ── */}
      {pageTab === 'ingredients' && (
        <IngredientsHub
          ingredients={ingredients}
          onIngredientsChange={loadIngredients}
          currentPrices={currentPrices}
          initialIngredientId={ingredientHubInitialId}
          initialTab={ingredientHubInitialTab}
          onInitialNavConsumed={() => {
            setIngredientHubInitialId(null);
            setIngredientHubInitialTab(null);
          }}
        />
      )}

      {/* ── Base Recipes tab ── */}
      {pageTab === 'base-recipes' && <BaseRecipesSection
        baseRecipes={allBaseRecipes}
        ingredients={ingredients}
        onRefresh={() => baseRecipeService.list().then(setAllBaseRecipes).catch(() => {})}
      />}

      {/* ── Suppliers tab ── */}
      {pageTab === 'suppliers' && <SuppliersHub
        suppliers={suppliers}
        onSuppliersChange={loadSuppliers}
      />}

      {/* ── Ingredients Modal — removed, replaced by IngredientsHub */}
      {false && <div className="modal fade" id="ingredientsModal" tabIndex="-1">
        <div className="modal-dialog modal-xl modal-fullscreen-sm-down modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header" style={{ backgroundColor: '#1b2638' }}>
              <h5 className="modal-title text-white">Manage Ingredients</h5>
              <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal"
                onClick={() => { setEditIngredient(null); setPriceIngredient(null); setPriceHistory([]); }} />
            </div>
            <div className="modal-body p-3">
              <div className="row g-3">

                {/* Ingredient list */}
                <div className="col-12 col-lg-8 d-flex flex-column">
                  <input
                    type="text"
                    className="form-control form-control-sm mb-2"
                    placeholder="Search ingredients…"
                    value={ingredientSearch}
                    onChange={e => setIngredientSearch(e.target.value)}
                  />
                  <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 460 }}>
                    <table className="table table-sm table-bordered mb-0" style={{ minWidth: 600 }}>
                      <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                          <th>Name</th>
                          <th>Manufacturer</th>
                          <th>Category</th>
                          <th>RM Code</th>
                          <th>Case Size</th>
                          <th style={{ width: 110 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredIngredients.map(ing => (
                          <tr
                            key={ing.id}
                            style={{
                              background: priceIngredient?.id === ing.id ? '#f0f7ff' : undefined,
                              outline: priceIngredient?.id === ing.id ? '2px solid #2f80ed' : undefined,
                            }}
                          >
                            <td>
                              {editIngredient?.id === ing.id
                                ? <input type="text" className="form-control form-control-sm" value={editIngredient.name} onChange={e => setEditIngredient({ ...editIngredient, name: e.target.value })} />
                                : ing.name}
                            </td>
                            <td>
                              {ing.Manufacturer?.name}
                              {editIngredient?.id === ing.id && (
                                <div className="text-muted" style={{ fontSize: 11 }}>Edit in the Ingredients tab</div>
                              )}
                            </td>
                            <td>
                              {editIngredient?.id === ing.id
                                ? <input type="text" className="form-control form-control-sm" placeholder="e.g. Flour" value={editIngredient.category || ''} onChange={e => setEditIngredient({ ...editIngredient, category: e.target.value })} />
                                : ing.category ? <span className="badge text-bg-secondary">{ing.category}</span> : <span className="text-muted" style={{ fontSize: 12 }}>—</span>}
                            </td>
                            <td>
                              {editIngredient?.id === ing.id
                                ? <input type="text" className="form-control form-control-sm" placeholder="e.g. FL-001" value={editIngredient.rawMaterialCode || ''} onChange={e => setEditIngredient({ ...editIngredient, rawMaterialCode: e.target.value })} />
                                : ing.rawMaterialCode ? <span className="badge text-bg-light text-dark border">{ing.rawMaterialCode}</span> : <span className="text-muted" style={{ fontSize: 12 }}>—</span>}
                            </td>
                            <td style={{ minWidth: 160 }}>
                              {editIngredient?.id === ing.id ? (
                                <div className="d-flex gap-1">
                                  <input
                                    type="number" min="0" step="0.001"
                                    className="form-control form-control-sm"
                                    style={{ width: 70 }}
                                    placeholder="Size"
                                    value={editIngredient.caseSize ?? ''}
                                    onChange={e => setEditIngredient({ ...editIngredient, caseSize: e.target.value })}
                                  />
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ width: 65 }}
                                    value={editIngredient.caseSizeUnit || 'kg'}
                                    onChange={e => setEditIngredient({ ...editIngredient, caseSizeUnit: e.target.value })}
                                  >
                                    <option value="kg">kg</option>
                                    <option value="lb">lb</option>
                                  </select>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{ width: 70 }}
                                    placeholder="bag…"
                                    value={editIngredient.caseLabel || ''}
                                    onChange={e => setEditIngredient({ ...editIngredient, caseLabel: e.target.value })}
                                  />
                                </div>
                              ) : ing.caseSize != null ? (
                                <span className="badge text-bg-light text-dark border">
                                  {Number(ing.caseSize).toLocaleString()} {ing.caseSizeUnit}{ing.caseLabel ? ` / ${ing.caseLabel}` : ''}
                                </span>
                              ) : (
                                <span className="text-muted" style={{ fontSize: 12 }}>—</span>
                              )}
                            </td>
                            <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                              <div className="d-flex gap-1 justify-content-center">
                                {editIngredient?.id === ing.id ? (
                                  <>
                                    <button className="btn btn-success btn-sm" onClick={() => handleIngredientUpdate(ing.id)}>Save</button>
                                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setEditIngredient(null)}>✕</button>
                                  </>
                                ) : (
                                  <button className="btn btn-outline-primary btn-sm" onClick={() => startEditIngredient(ing)}>Edit</button>
                                )}
                                <button
                                  className={`btn btn-sm ${priceIngredient?.id === ing.id ? 'btn-info' : 'btn-outline-secondary'}`}
                                  onClick={() => startViewPrices(ing)}
                                  title="View / record prices"
                                >$</button>
                                <button
                                  className={`btn btn-sm ${detailIngredient?.id === ing.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                                  onClick={() => startViewDetails(ing)}
                                  title="Sub-ingredients, allergens & supplier docs"
                                >📋</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Right panel: price tracker OR ingredient details */}
                <div className="col-12 col-lg-4">
                  {!priceIngredient && !detailIngredient ? (
                    <div className="d-flex align-items-center justify-content-center text-muted p-4"
                      style={{ fontSize: 13, border: '1px dashed #dee2e6', borderRadius: 6, minHeight: 120 }}>
                      Tap <strong className="mx-1">$</strong> for prices · <strong className="mx-1">📋</strong> for details
                    </div>
                  ) : detailIngredient ? (
                    <div className="p-3 border rounded bg-light d-flex flex-column gap-3" style={{ fontSize: 13 }}>
                      <div className="d-flex justify-content-between align-items-start">
                        <h6 className="mb-0">Details<br /><strong>{detailIngredient.name}</strong></h6>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => { setDetailIngredient(null); setIngDocs([]); }}>✕</button>
                      </div>

                      {/* Sub-ingredients */}
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <label className="form-label mb-0" style={{ fontSize: 12, fontWeight: 600 }}>Sub-ingredients</label>
                          <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11 }} onClick={addSubIngredient}>+ Add</button>
                        </div>
                        {detailSubIngredients.length === 0 ? (
                          <div className="text-muted" style={{ fontSize: 12 }}>None added yet.</div>
                        ) : (
                          <div className="d-flex flex-column gap-1">
                            {detailSubIngredients.map((sub, idx) => (
                              <div key={idx} className="d-flex gap-1 align-items-center">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="e.g. Pasteurized milk"
                                  value={sub.name}
                                  onChange={e => updateSubIngredient(idx, 'name', e.target.value)}
                                />
                                <button className="btn btn-sm btn-outline-danger" style={{ flexShrink: 0 }} onClick={() => removeSubIngredient(idx)}>✕</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Allergen flags */}
                      <div>
                        <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600 }}>Allergens (top-level)</label>
                        <div className="d-flex flex-wrap gap-2">
                          {ALLERGENS.map(a => (
                            <div key={a.key} className="form-check mb-0">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`allergen-${a.key}`}
                                checked={detailAllergenFlags.includes(a.key)}
                                onChange={() => toggleAllergen(a.key)}
                              />
                              <label className="form-check-label" htmlFor={`allergen-${a.key}`} style={{ fontSize: 12 }}>
                                {a.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>

                      <button
                        className="btn btn-sm btn-primary"
                        onClick={saveIngredientDetails}
                        disabled={savingDetails}
                      >
                        {savingDetails ? 'Saving…' : 'Save Details'}
                      </button>

                      {/* Supplier documents */}
                      <div>
                        <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600 }}>Supplier Documents</label>
                        {loadingIngDocs ? (
                          <div className="text-muted" style={{ fontSize: 12 }}>Loading…</div>
                        ) : ingDocs.length === 0 ? (
                          <div className="text-muted" style={{ fontSize: 12 }}>No supplier documents yet.</div>
                        ) : (
                          <div className="d-flex flex-column gap-1">
                            {ingDocs.map(doc => {
                              const today = new Date(); today.setHours(0,0,0,0);
                              const exp = doc.expiryDate ? new Date(doc.expiryDate + 'T12:00:00') : null;
                              const isExpired = exp && exp < today;
                              const isExpiring = exp && !isExpired && exp <= new Date(today.getTime() + 60*24*60*60*1000);
                              return (
                                <div key={doc.id} className="d-flex justify-content-between align-items-center p-1 border rounded bg-white">
                                  <div>
                                    <div style={{ fontSize: 12 }}>{doc.name}</div>
                                    {isExpired && <span className="badge bg-danger" style={{ fontSize: 10 }}>Expired</span>}
                                    {isExpiring && !isExpired && <span className="badge bg-warning text-dark" style={{ fontSize: 10 }}>Expiring soon</span>}
                                  </div>
                                  <a href={doc.fileUrl} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11 }}>View</a>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <a
                          href="/documents"
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-outline-secondary btn-sm w-100 mt-2"
                          style={{ fontSize: 11 }}
                        >
                          Upload / Manage in Documents →
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 border rounded bg-light d-flex flex-column gap-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <h6 className="mb-0">Price History<br /><strong>{priceIngredient.name}</strong></h6>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => { setPriceIngredient(null); setPriceHistory([]); }}>✕</button>
                      </div>

                      {/* Add new price */}
                      <div>
                        <div className="mb-2">
                          <label className="form-label mb-1" style={{ fontSize: 12 }}>Price / {newPrice.unit}</label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <input
                              type="number" min="0" step="0.0001"
                              className="form-control"
                              placeholder="0.0000"
                              value={newPrice.pricePerKg}
                              onChange={e => setNewPrice(p => ({ ...p, pricePerKg: e.target.value }))}
                            />
                            <select
                              className="input-group-text form-select form-select-sm"
                              style={{ width: 65 }}
                              value={newPrice.unit}
                              onChange={e => setNewPrice(p => ({ ...p, unit: e.target.value }))}
                            >
                              <option value="kg">kg</option>
                              <option value="lb">lb</option>
                            </select>
                          </div>
                        </div>
                        <div className="mb-2">
                          <label className="form-label mb-1" style={{ fontSize: 12 }}>Effective from</label>
                          <input
                            type="date" className="form-control form-control-sm"
                            value={newPrice.effectiveDate}
                            onChange={e => setNewPrice(p => ({ ...p, effectiveDate: e.target.value }))}
                          />
                        </div>
                        <div className="mb-2">
                          <label className="form-label mb-1" style={{ fontSize: 12 }}>Note <span className="text-muted">(optional)</span></label>
                          <input
                            type="text" className="form-control form-control-sm"
                            placeholder="e.g. new supplier"
                            value={newPrice.note}
                            onChange={e => setNewPrice(p => ({ ...p, note: e.target.value }))}
                          />
                        </div>
                        <button
                          className="btn btn-sm btn-primary w-100"
                          onClick={handleAddPrice}
                          disabled={!newPrice.pricePerKg || !newPrice.effectiveDate}
                        >
                          Record Price
                        </button>
                      </div>

                      {/* History table */}
                      <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 220 }}>
                        {loadingPrices ? (
                          <div className="text-muted" style={{ fontSize: 13 }}>Loading…</div>
                        ) : priceHistory.length === 0 ? (
                          <div className="text-muted" style={{ fontSize: 13 }}>No prices recorded yet.</div>
                        ) : (
                          <table className="table table-sm table-bordered mb-0">
                            <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                              <tr>
                                <th style={{ fontSize: 12 }}>Effective from</th>
                                <th style={{ fontSize: 12 }}>Price / kg</th>
                                <th style={{ fontSize: 12 }}>Note</th>
                              </tr>
                            </thead>
                            <tbody>
                              {priceHistory.map(p => (
                                <tr key={p.id}>
                                  <td style={{ fontSize: 12 }}>{p.effectiveDate}</td>
                                  <td style={{ fontSize: 12 }}>${Number(p.pricePerKg).toFixed(4)}</td>
                                  <td style={{ fontSize: 12 }} className="text-muted">{p.note || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>}

      {/* ── Price Calculator Modal ── */}
      {showCalcModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#1b2638' }}>
                <h5 className="modal-title text-white">$ Price Calculator</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => { setShowCalcModal(false); setCalcProductSearch(''); setCalcProductId(''); }} />
              </div>
              <div className="modal-body">
                <div className="row g-4">

                  {/* ── Left: source ── */}
                  <div className="col-12 col-lg-6">
                    <div className="btn-group w-100 mb-3" role="group">
                      <button
                        type="button"
                        className={`btn btn-sm ${calcMode === 'product' ? 'btn-dark' : 'btn-outline-dark'}`}
                        onClick={() => { setCalcMode('product'); setCalcCustomRecipe([]); calcResetPricing(); }}
                      >
                        Existing Product
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${calcMode === 'custom' ? 'btn-dark' : 'btn-outline-dark'}`}
                        onClick={() => { setCalcMode('custom'); setCalcProductId(''); setCalcProductSearch(''); calcResetPricing(); }}
                      >
                        Custom Recipe
                      </button>
                    </div>

                    {calcMode === 'product' ? (
                      <div>
                        <label className="form-label">Select Product</label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Type to search…"
                            value={calcProductSearch}
                            onChange={e => {
                              setCalcProductSearch(e.target.value);
                              setCalcProductDropdown(true);
                              if (!e.target.value) { setCalcProductId(''); calcResetPricing(); }
                            }}
                            onFocus={() => setCalcProductDropdown(true)}
                            onBlur={() => setTimeout(() => setCalcProductDropdown(false), 150)}
                            autoComplete="off"
                          />
                          {calcProductDropdown && (() => {
                            const q = calcProductSearch.toLowerCase();
                            const matches = activeProducts.filter(p => p.name.toLowerCase().includes(q));
                            if (!matches.length) return null;
                            return (
                              <ul className="list-group shadow-sm" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050, maxHeight: 220, overflowY: 'auto' }}>
                                {matches.map(p => (
                                  <li
                                    key={p.id}
                                    className="list-group-item list-group-item-action py-1 px-2"
                                    style={{ cursor: 'pointer', fontSize: 13 }}
                                    onMouseDown={() => {
                                      setCalcProductId(String(p.id));
                                      setCalcProductSearch(p.name);
                                      setCalcProductDropdown(false);
                                      calcResetPricing();
                                    }}
                                  >
                                    {p.name}
                                    {recipeCosts[p.id] != null && (
                                      <span className="text-muted ms-2" style={{ fontSize: 11 }}>${Number(recipeCosts[p.id]).toFixed(4)}/unit</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            );
                          })()}
                        </div>
                        {calcProductId && recipeCosts[String(calcProductId)] == null && (
                          <div className="text-warning mt-1" style={{ fontSize: 12 }}>No recipe cost available — add ingredient prices first.</div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <label className="form-label">Build Custom Recipe</label>
                        <RecipeEditor
                          ingredients={ingredients}
                          recipeItems={calcCustomRecipe}
                          onChange={items => { setCalcCustomRecipe(items); calcResetPricing(); }}
                          priceMap={currentPrices}
                        />
                      </div>
                    )}

                    {/* Base cost + extra cost */}
                    <div className="mt-3 p-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                      <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>Cost of Goods</div>
                      {calcBaseCost != null && (
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Recipe: ${Number(calcBaseCost).toFixed(4)}
                        </div>
                      )}
                      <div className="mt-2 mb-2">
                        <label className="form-label mb-1" style={{ fontSize: 12 }}>Additional Cost / unit <span className="text-muted">(e.g. packaging)</span></label>
                        <div className="input-group input-group-sm">
                          <span className="input-group-text">$</span>
                          <input
                            type="number" min="0" step="0.0001" className="form-control"
                            placeholder="0.0000" value={calcExtraCost}
                            onChange={e => { setCalcExtraCost(e.target.value); calcResetPricing(); }}
                          />
                        </div>
                      </div>
                      <div style={{ fontSize: 30, fontWeight: 900, lineHeight: 1.2 }}>
                        {calcTotalCost != null
                          ? <><span>${Number(calcTotalCost).toFixed(4)}</span><span className="text-muted ms-1" style={{ fontSize: 14 }}>/ unit total</span></>
                          : <span className="text-muted" style={{ fontSize: 20 }}>—</span>}
                      </div>
                    </div>
                    {/* Labor cost card */}
                    <div className="mt-2 p-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>Labor Cost / unit</div>
                        <div className="btn-group btn-group-sm">
                          <button
                            className={`btn btn-outline-secondary${calcLaborMode === 'db' ? ' active' : ''}`}
                            onClick={() => { setCalcLaborMode('db'); setCalcLaborCost(''); setCalcLaborDays(null); calcResetPricing(); }}
                          >From DB</button>
                          <button
                            className={`btn btn-outline-secondary${calcLaborMode === 'manual' ? ' active' : ''}`}
                            onClick={() => { setCalcLaborMode('manual'); setCalcLaborCost(''); setCalcLaborDays(null); calcResetPricing(); }}
                          >Manual</button>
                        </div>
                      </div>

                      {calcLaborMode === 'db' ? (
                        <div>
                          <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                            Select a period to fetch the average labor cost / unit across all products:
                          </div>
                          <div className="btn-group btn-group-sm mb-2">
                            {[30, 60, 90, 120, 360].map(d => (
                              <button
                                key={d}
                                className={`btn btn-outline-primary${calcLaborDays === d ? ' active' : ''}`}
                                onClick={() => fetchLaborCost(d)}
                                disabled={calcLaborLoading}
                              >{d}d</button>
                            ))}
                          </div>
                          {calcLaborLoading && (
                            <div className="text-muted" style={{ fontSize: 12 }}>Fetching…</div>
                          )}
                          {!calcLaborLoading && calcLaborDays && (
                            calcLaborCost
                              ? <div style={{ fontSize: 18, fontWeight: 700 }}>${Number(calcLaborCost).toFixed(4)} <span className="text-muted" style={{ fontSize: 12 }}>/ unit (last {calcLaborDays}d)</span></div>
                              : <div className="text-warning" style={{ fontSize: 12 }}>No labor data found for this period.</div>
                          )}
                        </div>
                      ) : (
                        <div className="input-group input-group-sm">
                          <span className="input-group-text">$</span>
                          <input
                            type="number" min="0" step="0.0001" className="form-control"
                            placeholder="0.0000" value={calcLaborCost}
                            onChange={e => { setCalcLaborCost(e.target.value); calcResetPricing(); }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── Right: pricing chain ── */}
                  <div className="col-12 col-lg-6">
                    <p className="text-muted" style={{ fontSize: 12 }}>
                      Enter either the price <strong>or</strong> the margin/markup — the other is computed automatically.
                      Changing the manufacturer price cascades down.
                    </p>

                    {/* Manufacturer */}
                    <div className="rounded p-3 mb-1" style={{ border: '1px solid #dee2e6', borderLeft: '4px solid #0d6efd' }}>
                      <div className="fw-bold mb-2" style={{ fontSize: 12, color: '#0d6efd', textTransform: 'uppercase', letterSpacing: 1 }}>Manufacturer</div>
                      {calcTotalCost != null && (
                        <div className="text-muted mb-2" style={{ fontSize: 12 }}>Cost: <strong>${Number(calcTotalCost).toFixed(4)}</strong></div>
                      )}
                      <div className="row g-2">
                        <div className="col-6">
                          <label className="form-label mb-1" style={{ fontSize: 12 }}>Sell Price</label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <input type="number" min="0" step="0.0001" className="form-control"
                              placeholder="0.0000" value={mfgSellPrice}
                              onChange={e => calcHandleMfgPrice(e.target.value)}
                              disabled={calcBaseCost == null} />
                          </div>
                        </div>
                        <div className="col-6">
                          <label className="form-label mb-1" style={{ fontSize: 12 }}>Gross Margin</label>
                          <div className="input-group input-group-sm">
                            <input type="number" min="0" max="99.9" step="0.1" className="form-control"
                              placeholder="0.0" value={mfgGM}
                              onChange={e => calcHandleMfgGM(e.target.value)}
                              disabled={calcBaseCost == null} />
                            <span className="input-group-text">%</span>
                          </div>
                        </div>
                      </div>
                      {calcBaseCost == null && (
                        <div className="text-muted mt-2" style={{ fontSize: 12 }}>Select a product or build a recipe to enable pricing.</div>
                      )}
                    </div>

                    <div className="text-center text-muted" style={{ fontSize: 18, lineHeight: 1.2 }}>↓</div>

                    {/* Distributor */}
                    <div className="rounded p-3 mb-1" style={{ border: '1px solid #dee2e6', borderLeft: '4px solid #6c757d' }}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="fw-bold" style={{ fontSize: 12, color: '#6c757d', textTransform: 'uppercase', letterSpacing: 1 }}>Distributor</div>
                        <span className="badge text-bg-secondary" style={{ fontSize: 10 }}>Optional</span>
                      </div>
                      {mfgSellPrice && (
                        <div className="text-muted mb-2" style={{ fontSize: 12 }}>Buys at: <strong>${Number(mfgSellPrice).toFixed(4)}</strong></div>
                      )}
                      <div className="row g-2">
                        <div className="col-6">
                          <label className="form-label mb-1" style={{ fontSize: 12 }}>Sell Price</label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <input type="number" min="0" step="0.0001" className="form-control"
                              placeholder="0.0000" value={distSellPrice}
                              onChange={e => calcHandleDistPrice(e.target.value)}
                              disabled={!mfgSellPrice} />
                          </div>
                        </div>
                        <div className="col-6">
                          <label className="form-label mb-1" style={{ fontSize: 12 }}>Markup</label>
                          <div className="input-group input-group-sm">
                            <input type="number" min="0" step="0.1" className="form-control"
                              placeholder="0.0" value={distMarkup}
                              onChange={e => calcHandleDistMarkup(e.target.value)}
                              disabled={!mfgSellPrice} />
                            <span className="input-group-text">%</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>Leave blank → retailer buys direct from manufacturer.</div>
                    </div>

                    <div className="text-center text-muted" style={{ fontSize: 18, lineHeight: 1.2 }}>↓</div>

                    {/* Retailer */}
                    <div className="rounded p-3 mb-3" style={{ border: '1px solid #dee2e6', borderLeft: '4px solid #198754' }}>
                      <div className="fw-bold mb-2" style={{ fontSize: 12, color: '#198754', textTransform: 'uppercase', letterSpacing: 1 }}>Retailer</div>
                      {(distSellPrice || mfgSellPrice) && (
                        <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                          Buys at: <strong>${Number(distSellPrice || mfgSellPrice).toFixed(4)}</strong>
                          <span className="ms-1">{distSellPrice ? '(from distributor)' : '(direct from manufacturer)'}</span>
                        </div>
                      )}
                      <div className="row g-2">
                        <div className="col-12">
                          <label className="form-label mb-1" style={{ fontSize: 12 }}>Retail Price</label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <input type="number" min="0" step="0.0001" className="form-control"
                              placeholder="0.0000" value={retailPrice}
                              onChange={e => calcHandleRetailPrice(e.target.value)}
                              disabled={!mfgSellPrice} />
                          </div>
                        </div>
                      </div>
                      <div className="row g-2 mt-1">
                        <div className="col-6">
                          <label className="form-label mb-1" style={{ fontSize: 12 }}>Markup</label>
                          <div className="input-group input-group-sm">
                            <input type="number" min="0" step="0.1" className="form-control"
                              placeholder="0.0" value={retailMarkup}
                              onChange={e => calcHandleRetailMarkup(e.target.value)}
                              disabled={!mfgSellPrice} />
                            <span className="input-group-text">%</span>
                          </div>
                        </div>
                        <div className="col-6">
                          <label className="form-label mb-1" style={{ fontSize: 12 }}>Profit Margin</label>
                          <div className="input-group input-group-sm">
                            <input type="number" min="0" max="99.9" step="0.1" className="form-control"
                              placeholder="0.0" value={retailMargin}
                              onChange={e => calcHandleRetailMargin(e.target.value)}
                              disabled={!mfgSellPrice} />
                            <span className="input-group-text">%</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                        Margin = (retail price − wholesale cost) ÷ retail price. Most retailers plan around margin, not markup.
                      </div>
                    </div>

                    {/* Summary */}
                    {(mfgSellPrice || distSellPrice || retailPrice) && (
                      <div className="p-3 rounded" style={{ background: '#f0f7ff', border: '1px solid #b8d4f5' }}>
                        <div className="fw-bold mb-2" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Price Chain Summary</div>
                        <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                          <tbody>
                            {calcBaseCost != null && (
                              <tr>
                                <td className="text-muted border-0">Cost of goods</td>
                                <td className="fw-semibold border-0">${Number(calcBaseCost).toFixed(4)}</td>
                                <td className="border-0"></td>
                              </tr>
                            )}
                            {mfgSellPrice && (
                              <tr>
                                <td className="border-0">Manufacturer sells at</td>
                                <td className="fw-bold border-0">${Number(mfgSellPrice).toFixed(4)}</td>
                                <td className="text-muted border-0">{mfgGM ? `GM ${mfgGM}%` : ''}</td>
                              </tr>
                            )}
                            {distSellPrice && (
                              <tr>
                                <td className="border-0">Distributor sells at</td>
                                <td className="fw-bold border-0">${Number(distSellPrice).toFixed(4)}</td>
                                <td className="text-muted border-0">{distMarkup ? `Markup ${distMarkup}%` : ''}</td>
                              </tr>
                            )}
                            {retailPrice && (
                              <tr>
                                <td className="border-0">Retail price</td>
                                <td className="fw-bold text-success border-0" style={{ fontSize: 18 }}>${Number(retailPrice).toFixed(2)}</td>
                                <td className="text-muted border-0">
                                  {retailMarkup ? `Markup ${retailMarkup}%` : ''}
                                  {retailMarkup && retailMargin ? ' · ' : ''}
                                  {retailMargin ? `Margin ${retailMargin}%` : ''}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={calcResetPricing}>Reset Prices</button>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowCalcModal(false); setCalcProductSearch(''); setCalcProductId(''); }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Product Edit Modal ── */}
      {showEditModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#1b2638' }}>
                <h5 className="modal-title text-white">Edit Product — {editProductName}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3 mb-4 align-items-end">
                  <div className="col-md-7">
                    <label className="form-label">Product Name</label>
                    <input type="text" className="form-control" value={editProductName} onChange={e => setEditProductName(e.target.value)} />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Type</label>
                    <select
                      className="form-select"
                      value={editProductType}
                      onChange={e => {
                        const t = e.target.value;
                        setEditProductType(t);
                        if (t === 'resale' && (editProductTab === 'recipe' || editProductTab === 'nutrition')) {
                          setEditProductTab('packaging');
                        }
                      }}
                    >
                      <option value="manufactured">Manufactured</option>
                      <option value="resale">Resale / Service</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Regulated By</label>
                    <select className="form-select" value={editRegulatoryAgency} onChange={e => setEditRegulatoryAgency(e.target.value)}>
                      <option value="fda">FDA</option>
                      <option value="usda">USDA (meat/poultry)</option>
                    </select>
                  </div>
                  <div className="col">
                    <label className="form-label mb-1" style={{ fontSize: 13 }}>Process</label>
                    <select className="form-select form-select-sm" value={editProductionFlowType} onChange={e => setEditProductionFlowType(e.target.value)}>
                      <option value="baking">Baking</option>
                      <option value="pasteurization">Pasteurization</option>
                    </select>
                  </div>
                  <div className="col">
                    <label className="form-label mb-1" style={{ fontSize: 13 }}>Default units <span className="text-muted" style={{ fontSize: 11 }}>/ cart</span></label>
                    <input
                      type="number" min="1" step="1" className="form-control form-control-sm"
                      placeholder="e.g. 40"
                      value={editDefaultUnitsPerCart}
                      onChange={e => setEditDefaultUnitsPerCart(e.target.value)}
                    />
                  </div>
                  {editProductType === 'manufactured' && (
                  <div className="col">
                    <label className="form-label mb-1" style={{ fontSize: 13 }}>Ingredients wt / unit <span className="text-muted" style={{ fontSize: 11 }}>(recipe)</span></label>
                    <div className="input-group input-group-sm">
                      <input
                        type="text" className="form-control"
                        readOnly
                        value={recipeWeights[editProductId] != null ? Number(recipeWeights[editProductId]).toFixed(3) : '—'}
                        style={{ background: '#f8f9fa', cursor: 'default' }}
                      />
                      <span className="input-group-text">kg</span>
                    </div>
                  </div>
                  )}
                  <div className="col">
                    <label className="form-label mb-1" style={{ fontSize: 13 }}>Net weight / unit <span className="text-muted" style={{ fontSize: 11 }}>(BOL)</span></label>
                    <div className="input-group input-group-sm">
                      <input
                        type="number" min="0" step="0.001" className="form-control"
                        placeholder="e.g. 0.5"
                        value={editUnitNetWeightValue}
                        onChange={e => setEditUnitNetWeightValue(e.target.value)}
                      />
                      <select className="form-select" style={{ width: 72, flex: '0 0 72px' }} value={editUnitNetWeightUnit} onChange={e => setEditUnitNetWeightUnit(e.target.value)}>
                        <option value="lb">lb</option>
                        <option value="kg">kg</option>
                      </select>
                    </div>
                  </div>
                  <div className="col">
                    <label className="form-label mb-1" style={{ fontSize: 13 }}>Shelf life <span className="text-muted" style={{ fontSize: 11 }}>(labels)</span></label>
                    <div className="input-group input-group-sm">
                      <input
                        type="number" min="1" step="1" className="form-control"
                        placeholder="e.g. 365"
                        value={editShelfLifeDays}
                        onChange={e => setEditShelfLifeDays(e.target.value)}
                      />
                      <span className="input-group-text">days</span>
                    </div>
                  </div>
                  <div className="col">
                    <label className="form-label mb-1" style={{ fontSize: 13 }}>SKU <span className="text-muted" style={{ fontSize: 11 }}>(optional)</span></label>
                    <input
                      type="text" className="form-control form-control-sm"
                      placeholder="e.g. SKU-001"
                      value={editSku}
                      onChange={e => setEditSku(e.target.value)}
                    />
                  </div>
                </div>
                {editProductType === 'manufactured' && editRegulatoryAgency !== 'usda' && editRecipe.some(r => {
                  const ing = ingredients.find(i => String(i.id) === String(r.ingredientId));
                  return ing?.isMeatOrPoultry;
                }) && (
                  <div className="alert alert-warning py-1 px-2 mb-3" style={{ fontSize: 12 }}>
                    ⚠ This recipe contains an ingredient flagged "meat or poultry" but the product
                    is classified as <strong>FDA</strong> above — double-check whether it should be
                    regulated by USDA/FSIS instead.
                  </div>
                )}
                {editProductType === 'manufactured' && (
                <div className="row g-2 mb-3">
                  <div className="col-12">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="editNoBakeToggle"
                        checked={editNoBake}
                        onChange={e => setEditNoBake(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="editNoBakeToggle" style={{ fontSize: 13 }}>
                        No-Bake Product
                        <span className="text-muted ms-2" style={{ fontSize: 11 }}>
                          (no oven temperature required — skips baking CCP, keeps freezer/packaging flow)
                        </span>
                      </label>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="editIsIntermediateToggle"
                        checked={editIsIntermediate}
                        onChange={e => setEditIsIntermediate(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="editIsIntermediateToggle" style={{ fontSize: 13 }}>
                        Intermediate product (internal use only)
                        <span className="text-muted ms-2" style={{ fontSize: 11 }}>
                          (e.g. a cooked filling/sauce — never sold directly; hidden from price lists, Shopify, BOL, and order forms. Runs through Prep CCP instead of Baking CCP.)
                        </span>
                      </label>
                    </div>
                    {editIsIntermediate && (() => {
                      const linkedIngredient = ingredients.find(i => i.sourceProductId === editProductId);
                      if (linkedIngredient) {
                        return (
                          <div className="mt-2 ps-4">
                            <span className="text-success" style={{ fontSize: 12 }}>
                              ✓ Linked ingredient: <strong>{linkedIngredient.name}</strong>
                            </span>
                            <div className="form-text" style={{ fontSize: 11 }}>
                              When a Prep CCP batch for this product is verified, its real output is
                              auto-credited into this ingredient's stock.
                            </div>
                          </div>
                        );
                      }
                      return (
                      <div className="mt-2 ps-4">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={creatingLinkedIngredient}
                          onClick={async () => {
                            setCreatingLinkedIngredient(true);
                            setLinkedIngredientStatus('');
                            try {
                              await ingredientService.createIngredient({
                                name: `${editProductName} (prepared)`,
                                sourceProductId: editProductId,
                              });
                              setLinkedIngredientStatus('success');
                              setIngredients(await ingredientService.getIngredients());
                            } catch (err) {
                              setLinkedIngredientStatus(err?.response?.data?.message || 'Failed to create ingredient.');
                            } finally {
                              setCreatingLinkedIngredient(false);
                            }
                          }}
                        >
                          {creatingLinkedIngredient ? 'Creating…' : '+ Create linked ingredient'}
                        </button>
                        {linkedIngredientStatus === 'success' && (
                          <span className="text-success ms-2" style={{ fontSize: 12 }}>
                            ✓ Created — other products can now use it in their recipe like any ingredient.
                          </span>
                        )}
                        {linkedIngredientStatus && linkedIngredientStatus !== 'success' && (
                          <span className="text-danger ms-2" style={{ fontSize: 12 }}>{linkedIngredientStatus}</span>
                        )}
                        <div className="form-text" style={{ fontSize: 11 }}>
                          Creates an ingredient named "{editProductName} (prepared)" linked to this product.
                          When a Prep CCP batch for this product is verified, its real output is
                          auto-credited into this ingredient's stock. Skip if you already created one.
                        </div>
                      </div>
                      );
                    })()}
                  </div>
                </div>
                )}
                {/* Recipe / Nutrition / Packaging tabs */}
                <ul className="nav nav-tabs mb-3" style={{ fontSize: '13px' }}>
                  {editProductType === 'manufactured' && (
                    <li className="nav-item">
                      <button
                        className={`nav-link py-1 px-3 ${editProductTab === 'recipe' ? 'active' : ''}`}
                        onClick={() => switchProductTab('recipe')}
                      >
                        Recipe
                      </button>
                    </li>
                  )}
                  {editProductType === 'manufactured' && (
                    <li className="nav-item">
                      <button
                        className={`nav-link py-1 px-3 ${editProductTab === 'nutrition' ? 'active' : ''}`}
                        onClick={() => switchProductTab('nutrition')}
                      >
                        🧪 Nutrition Facts
                      </button>
                    </li>
                  )}
                  <li className="nav-item">
                    <button
                      className={`nav-link py-1 px-3 ${editProductTab === 'packaging' ? 'active' : ''}`}
                      onClick={() => switchProductTab('packaging')}
                    >
                      📦 Packaging
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link py-1 px-3 ${editProductTab === 'techsheet' ? 'active' : ''}`}
                      onClick={() => switchProductTab('techsheet')}
                    >
                      📄 Tech Sheet
                    </button>
                  </li>
                </ul>

                {editProductTab === 'recipe' && (
                  <>
                    {/* Base recipe references — shown above direct ingredients */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label mb-0 fw-semibold">
                          Base Recipes <small className="text-muted fw-normal">— shared sub-recipes (e.g. dough)</small>
                        </label>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => setEditBaseRecipes(r => [...r, { baseRecipeId: '', displayQty: '', unit: 'g' }])}
                        >
                          + Add
                        </button>
                      </div>
                      {editBaseRecipes.length === 0 ? (
                        <div className="text-muted small">No base recipes assigned.</div>
                      ) : (
                        editBaseRecipes.map((ref, idx) => {
                          const brCost = computeBaseRecipeCostPerUnit(ref, allBaseRecipes, currentPrices);
                          return (
                          <div key={idx} className="d-flex gap-2 mb-2 align-items-center">
                            <div style={{ flex: 3 }}>
                              <SearchableSelect
                                options={allBaseRecipes.map(br => ({ value: String(br.id), label: br.name }))}
                                value={ref.baseRecipeId}
                                onChange={v => setEditBaseRecipes(rs => rs.map((r, i) => i === idx ? { ...r, baseRecipeId: v } : r))}
                                placeholder="Select base recipe…"
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div className="input-group">
                                <input
                                  type="number"
                                  className="form-control"
                                  min="0"
                                  step={ref.unit === 'g' ? '1' : '0.001'}
                                  placeholder="0"
                                  value={ref.displayQty}
                                  onChange={e => setEditBaseRecipes(rs => rs.map((r, i) => i === idx ? { ...r, displayQty: e.target.value } : r))}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  style={{ minWidth: 42 }}
                                  onClick={() => toggleBrUnit(setEditBaseRecipes, idx)}
                                  title="Switch unit"
                                >
                                  {ref.unit}
                                </button>
                                {brCost != null && (
                                  <span className="input-group-text text-success fw-semibold" style={{ fontSize: 13 }}>
                                    ${brCost.toFixed(4)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={() => setEditBaseRecipes(rs => rs.filter((_, i) => i !== idx))}
                              title="Remove"
                            >&times;</button>
                          </div>
                        );
                        })
                      )}
                    </div>
                    <div className="pt-3" style={{ borderTop: '1px solid #dee2e6' }}>
                      <label className="form-label">
                        Direct Ingredients <small className="text-muted">— expected quantity per unit produced</small>
                      </label>
                      <RecipeEditor
                        ingredients={ingredients}
                        recipeItems={editRecipe}
                        onChange={setEditRecipe}
                        priceMap={currentPrices}
                        baseRecipeCost={editBaseRecipes.reduce((acc, ref) => {
                          const c = computeBaseRecipeCostPerUnit(ref, allBaseRecipes, currentPrices);
                          return c != null ? (acc || 0) + c : acc;
                        }, null)}
                      />
                    </div>
                  </>
                )}

                {editProductTab === 'nutrition' && editProductId && (
                  <NutritionFactsPanel
                    product={{ id: editProductId, name: editProductName }}
                    productIngredients={editRecipe.map(r => {
                      const ing = ingredients.find(i => String(i.id) === String(r.ingredientId));
                      return { ingredientId: r.ingredientId, name: ing?.name || `Ingredient #${r.ingredientId}`, expectedQuantityKg: r.expectedQuantityKg };
                    })}
                    onNavigateToIngredient={navigateToIngredient}
                  />
                )}

                {editProductTab === 'packaging' && (
                  <div>
                    {/* Unit toggle */}
                    <div className="d-flex align-items-center gap-3 mb-3 pb-2" style={{ borderBottom: '1px solid #dee2e6' }}>
                      <span className="text-muted" style={{ fontSize: 12 }}>Display unit:</span>
                      <div className="btn-group btn-group-sm">
                        <button type="button" className={`btn ${packagingUnit === 'lb' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => togglePackagingUnit('lb')}>lb</button>
                        <button type="button" className={`btn ${packagingUnit === 'kg' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => togglePackagingUnit('kg')}>kg</button>
                      </div>
                      <span className="text-muted" style={{ fontSize: 11 }}>Values are stored in kg</span>
                    </div>

                    {/* Wrapper weight */}
                    <div className="mb-3">
                      <label className="form-label mb-1" style={{ fontSize: 13, fontWeight: 600 }}>
                        Wrapper / film weight per unit
                        <span className="text-muted fw-normal ms-1" style={{ fontSize: 12 }}>(plastic film, common to all presentations)</span>
                      </label>
                      <div className="input-group input-group-sm" style={{ maxWidth: 220 }}>
                        <input
                          type="number" min="0" step="0.00001" className="form-control"
                          placeholder={packagingUnit === 'lb' ? 'e.g. 0.029' : 'e.g. 0.013'}
                          value={editWrapperWeight}
                          onChange={e => setEditWrapperWeight(e.target.value)}
                        />
                        <span className="input-group-text">{packagingUnit}</span>
                      </div>
                    </div>

                    {/* QA reference photo — used by the AI Visual Inspection SQF checklist field
                        type to judge a freshly-photographed production sample against. */}
                    <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #dee2e6' }}>
                      <label className="form-label mb-1" style={{ fontSize: 13, fontWeight: 600 }}>
                        QA Reference Photo
                        <span className="text-muted fw-normal ms-1" style={{ fontSize: 12 }}>(what a correctly-made unit should look like — used by AI Visual Inspection)</span>
                      </label>
                      <div className="d-flex align-items-start gap-3">
                        <div
                          className={`d-flex align-items-center justify-content-center text-center ${qaReferenceDragging ? 'border-primary bg-primary bg-opacity-10' : 'border-dashed'}`}
                          style={{
                            width: 160, height: 160, flex: '0 0 auto', cursor: qaReferenceUploading ? 'wait' : 'pointer',
                            borderRadius: 6, border: qaReferenceDragging ? '2px solid' : '1px dashed #adb5bd',
                            backgroundColor: editQaReferenceImageUrl ? 'transparent' : '#fff', overflow: 'hidden', position: 'relative',
                          }}
                          onClick={() => !qaReferenceUploading && qaReferenceFileInputRef.current?.click()}
                          onDragOver={e => { e.preventDefault(); setQaReferenceDragging(true); }}
                          onDragLeave={() => setQaReferenceDragging(false)}
                          onDrop={handleQaReferenceDrop}
                        >
                          {qaReferenceUploading ? (
                            <span className="spinner-border spinner-border-sm text-muted" />
                          ) : editQaReferenceImageUrl ? (
                            <img src={editQaReferenceImageUrl} alt="QA reference" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div className="text-muted px-2" style={{ fontSize: 12 }}>
                              <div style={{ fontSize: 28 }}>📷</div>
                              Drag & drop photo<br />or click to upload
                            </div>
                          )}
                        </div>
                        <input ref={qaReferenceFileInputRef} type="file" accept="image/jpeg,image/jpg,image/png"
                          style={{ display: 'none' }} onChange={handleQaReferenceFileChange} disabled={qaReferenceUploading} />
                        <div style={{ flex: 1 }}>
                          {editQaReferenceImageUrl && (
                            <button type="button" className="btn btn-link btn-sm text-danger p-0 mb-2"
                              onClick={() => setEditQaReferenceImageUrl('')} disabled={qaReferenceUploading}>
                              Remove photo
                            </button>
                          )}
                          <textarea className="form-control form-control-sm" rows={2}
                            placeholder="Optional notes on what 'correct' looks like (e.g. golden-brown edges, evenly topped, round)"
                            value={editQaReferenceNotes} onChange={e => setEditQaReferenceNotes(e.target.value)} />
                        </div>
                      </div>
                    </div>

                    {/* Presentations table */}
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fw-semibold" style={{ fontSize: 13 }}>Retail Presentations</span>
                      <div className="d-flex gap-2 align-items-center">
                        {/* Clone from another product */}
                        <select
                          className="form-select form-select-sm"
                          style={{ width: 200 }}
                          value={cloneFromProductId}
                          onChange={e => setCloneFromProductId(e.target.value)}
                        >
                          <option value="">Clone from product…</option>
                          {products.filter(p => p.id !== editProductId).map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        {cloneFromProductId && (
                          <button className="btn btn-outline-secondary btn-sm" onClick={clonePresentationsFrom}>
                            Clone
                          </button>
                        )}
                        <button className="btn btn-primary btn-sm" onClick={() => setPresAddingNew(true)}>
                          + Add
                        </button>
                      </div>
                    </div>

                    <table className="table table-sm table-bordered mb-2" style={{ fontSize: 13 }}>
                      <thead className="table-light">
                        <tr>
                          <th>Presentation name</th>
                          <th style={{ width: 130 }}>Units / retail box</th>
                          <th style={{ width: 150 }}>Retail box wt ({packagingUnit})</th>
                          <th style={{ width: 150 }}>Master case wt ({packagingUnit})</th>
                          <th style={{ width: 160 }}>Case GTIN</th>
                          <th style={{ width: 150 }}>Unit GTIN</th>
                          <th style={{ width: 100 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {editPresentations.map(pres => (
                          presEditRow?.id === pres.id ? (
                            <tr key={pres.id} style={{ background: '#fffbe6' }}>
                              <td>
                                <input className="form-control form-control-sm" value={presEditRow.name}
                                  onChange={e => setPresEditRow(r => ({ ...r, name: e.target.value }))} />
                              </td>
                              <td>
                                <input type="number" min="0.0001" step="0.0001" className="form-control form-control-sm"
                                  value={presEditRow.unitsPerRetailBox}
                                  onChange={e => setPresEditRow(r => ({ ...r, unitsPerRetailBox: e.target.value }))} />
                              </td>
                              <td>
                                <input type="number" min="0" step="0.00001" className="form-control form-control-sm"
                                  value={presEditRow.retailBoxWeight}
                                  onChange={e => setPresEditRow(r => ({ ...r, retailBoxWeight: e.target.value }))} />
                              </td>
                              <td>
                                <input type="number" min="0" step="0.00001" className="form-control form-control-sm"
                                  value={presEditRow.masterCaseWeight}
                                  onChange={e => setPresEditRow(r => ({ ...r, masterCaseWeight: e.target.value }))} />
                              </td>
                              <td>
                                <input type="text" className="form-control form-control-sm font-monospace"
                                  placeholder="14-digit GTIN"
                                  maxLength={14}
                                  value={presEditRow.gtin || ''}
                                  onChange={e => setPresEditRow(r => ({ ...r, gtin: e.target.value.replace(/\D/g, '') }))} />
                              </td>
                              <td>
                                <input type="text" className="form-control form-control-sm font-monospace"
                                  placeholder="12-digit UPC-A"
                                  maxLength={12}
                                  value={presEditRow.unitGtin || ''}
                                  onChange={e => setPresEditRow(r => ({ ...r, unitGtin: e.target.value.replace(/\D/g, '') }))} />
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                <button className="btn btn-success btn-sm me-1" onClick={() => savePresentation(presEditRow)}>✓</button>
                                <button className="btn btn-outline-secondary btn-sm" onClick={() => setPresEditRow(null)}>✕</button>
                              </td>
                            </tr>
                          ) : (
                            <tr key={pres.id}>
                              <td>{pres.name}</td>
                              <td className="text-center">{pres.unitsPerRetailBox}</td>
                              <td className="text-end">{pres.retailBoxWeightKg != null ? fromKg(pres.retailBoxWeightKg) : <span className="text-muted">—</span>}</td>
                              <td className="text-end">{pres.masterCaseWeightKg != null ? fromKg(pres.masterCaseWeightKg) : <span className="text-muted">—</span>}</td>
                              <td className="font-monospace" style={{ fontSize: 12 }}>{pres.gtin || <span className="text-muted">—</span>}</td>
                              <td className="font-monospace" style={{ fontSize: 12 }}>{pres.unitGtin || <span className="text-muted">—</span>}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                <button className="btn btn-outline-primary btn-sm me-1" onClick={() => setPresEditRow({
                                  id: pres.id,
                                  name: pres.name,
                                  unitsPerRetailBox: String(pres.unitsPerRetailBox),
                                  retailBoxWeight: fromKg(pres.retailBoxWeightKg),
                                  masterCaseWeight: fromKg(pres.masterCaseWeightKg),
                                  gtin: pres.gtin || '',
                                  unitGtin: pres.unitGtin || '',
                                })}>Edit</button>
                                <button className="btn btn-outline-danger btn-sm" onClick={() => deletePresentation(pres.id)}>Del</button>
                              </td>
                            </tr>
                          )
                        ))}
                        {presAddingNew && (
                          <tr style={{ background: '#f0fff4' }}>
                            <td>
                              <input className="form-control form-control-sm" placeholder="e.g. Single Pack"
                                value={presNewRow.name}
                                onChange={e => setPresNewRow(r => ({ ...r, name: e.target.value }))} />
                            </td>
                            <td>
                              <input type="number" min="0.0001" step="0.0001" className="form-control form-control-sm"
                                value={presNewRow.unitsPerRetailBox}
                                onChange={e => setPresNewRow(r => ({ ...r, unitsPerRetailBox: e.target.value }))} />
                            </td>
                            <td>
                              <input type="number" min="0" step="0.00001" className="form-control form-control-sm"
                                placeholder={packagingUnit}
                                value={presNewRow.retailBoxWeight}
                                onChange={e => setPresNewRow(r => ({ ...r, retailBoxWeight: e.target.value }))} />
                            </td>
                            <td>
                              <input type="number" min="0" step="0.00001" className="form-control form-control-sm"
                                placeholder={packagingUnit}
                                value={presNewRow.masterCaseWeight}
                                onChange={e => setPresNewRow(r => ({ ...r, masterCaseWeight: e.target.value }))} />
                            </td>
                            <td>
                              <input type="text" className="form-control form-control-sm font-monospace"
                                placeholder="14-digit GTIN"
                                maxLength={14}
                                value={presNewRow.gtin}
                                onChange={e => setPresNewRow(r => ({ ...r, gtin: e.target.value.replace(/\D/g, '') }))} />
                            </td>
                            <td>
                              <input type="text" className="form-control form-control-sm font-monospace"
                                placeholder="12-digit UPC-A"
                                maxLength={12}
                                value={presNewRow.unitGtin}
                                onChange={e => setPresNewRow(r => ({ ...r, unitGtin: e.target.value.replace(/\D/g, '') }))} />
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <button className="btn btn-success btn-sm me-1" onClick={addPresentation}>✓</button>
                              <button className="btn btn-outline-secondary btn-sm" onClick={() => setPresAddingNew(false)}>✕</button>
                            </td>
                          </tr>
                        )}
                        {editPresentations.length === 0 && !presAddingNew && (
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-3" style={{ fontSize: 13 }}>
                              No presentations yet. Click "+ Add" or clone from another product.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                      Presentations are used on the Bill of Lading to auto-calculate gross weight (net + wrapper + retail box + master case + pallet share).
                    </p>
                  </div>
                )}

                {editProductTab === 'techsheet' && editProductId && (
                  <TechSheetPanel productId={editProductId} productName={editProductName} onDirtyChange={setTechSheetDirty} />
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!pendingProductTab}
        title="Unsaved Tech Sheet Changes"
        message="You have unsaved changes in the Tech Sheet draft. If you switch tabs now, your work will be lost. Save the draft first, or switch and discard."
        confirmLabel="Switch anyway"
        cancelLabel="Stay and save"
        variant="warning"
        onConfirm={() => { setTechSheetDirty(false); setEditProductTab(pendingProductTab); setPendingProductTab(null); }}
        onCancel={() => setPendingProductTab(null)}
      />
    </div>
  );
}

export default AdminProductPage;
